import React, { useEffect, useLayoutEffect, useState, useRef, useCallback, useMemo } from 'react';
import { connect, useSelector } from 'react-redux';
import { FaSlidersH } from 'react-icons/fa';
import history from '../history';
import { getProductConfig } from '../backend';
import { getNvrTimestamp, logoutUser } from '../actions';
import {
  runMcpbSearch, setMcpbSlotCamera, refetchMcpbEvents,
  setMcpbConf, setMcpbSlots, setMcpbPlayhead, setMcpbEventFlag, loadViewIntoMcpb,
  setMcpbSpan, pageMcpbWindow, setMcpbFilters, getMcpbPresence, verifyPlaybackSession,
} from '../actions/MCPlaybackActions';
import { autoSelectDefaultView, setCameraArrangement, revertViewToPristine } from '../actions/ViewActions';
import { chipTime, chipDate, windowSeconds, windowLabel, addSeconds } from '../util/mcpbTime';
import { firstPlayableOffset, unionTrackClips, clipAtOffset } from '../util/mcpbClips';
import { cameraDisplayLabel } from '../util/cameraLabel';
import { isMultiServerSession } from '../util/sessionMode';
import { filterTrackEvents } from '../util/mcpbFilter';
import { anyCellCanStall, cellBecameReady, heldCellsAllProgressing, slotCanStall } from '../util/mcpbSync';
import { normalizeSlotCamera } from '../util/cameraDrawer';
import { swapSlots, removeAndResize, deriveConf } from '../util/mcpbSlots';
import { confToNumber } from '../util/layouts';
import { slotPrefersPro2 } from '../util/playbackProfile';
import { enabledSetCamViews, swapVisibleInFull, getVisibleCameras, assignVisibleInFull, removeVisibleInFull } from '../util/camSets';
import { changeGridLayout, camViewChange } from '../actions/CamButtonsActions';
import CamButtonsView from '../live/CamButtonsView';
import { DndContext, DragOverlay, MouseSensor, TouchSensor, useSensor, useSensors, closestCenter } from '@dnd-kit/core';
import { resolveDrop } from '../util/mcpbDrag';
import { deriveBulkItems, ARCHIVE_FLAG, EXPORT_FLAG } from '../util/mcpbActions';
import { downloadComposite } from '../util/screenshotHelper';
import { useTimelinePlayhead } from '../hooks/useTimelinePlayhead';
import { useClipDownload } from '../hooks/useClipDownload';
import { useToasts } from '../hooks/useToasts';
import { useSavedLayouts } from '../hooks/useSavedLayouts';
import { getVideoInfoPref, setVideoInfoPref } from '../util/mcpbVideoInfoPref';
import { getQuickActionsPref, setQuickActionsPref } from '../util/mcpbQuickActionsPref';
import { FaListUl, FaEllipsisH, FaCaretDown, FaAngleLeft, FaAngleRight } from 'react-icons/fa';
import { MdUnfoldMore, MdUnfoldLess } from 'react-icons/md';
import MobileTopBar from '../common/MobileTopBar';
import DesktopHeader from '../live/DesktopHeader';
import JumpControl from '../live/JumpControl';
import MobileBottomNav from '../common/MobileBottomNav';
import MobileMoreMenu from '../common/MobileMoreMenu';
import SlotCell from './SlotCell';
import Transport from './Transport';
import ActionsMenu from './ActionsMenu';
import MoreSheet from './MoreSheet';
import Toasts from './Toasts';
import DtModal from './DtModal';
import CameraDrawer from './CameraDrawer';
import MCPlaybackRail from './MCPlaybackRail';
import { formatNvrClock } from '../util/formatClock';
import RailClock from '../common/RailClock';
import SystemInfoSheet from '../live/SystemInfoSheet';
import ViewList from '../live/ViewList';
import Sheet from '../common/Sheet';
import CameraPicker from './CameraPicker';
import Timeline, { TimelineVisibilityRow } from './Timeline';
import MCPlaybackRibbon from './MCPlaybackRibbon';
import { arrangementsEqual } from '../util/arrangePages';
import './mcplaybackStyle.css';

// Behavior gates (matchMedia — layout itself stays CSS-driven). Portrait tier (mobile AND tablet, ≤1023 —
// matches dragViewport.isMobilePortrait after S19) = the base tier; phone-landscape = the rail tier. BOTH
// gate the per-cell action-row collapse (small cells → ••• even when always-show is on); phone-landscape
// also gates whether the landscape rail MOUNTS, so portrait/tests don't render its reused ribbon + transport
// a second time (#86bb7phhh).
// #86bb7phhh S20: broadened 767→1023 so tablet-portrait cells ALSO collapse to ••• like mobile (decision 2 — unify).
const MOBILE_PORTRAIT_MQ = '(max-width: 1023px) and (orientation: portrait)';
const PHONE_LANDSCAPE_MQ = '(orientation: landscape) and (max-height: 500px) and (max-width: 932px)';
// Wide "no-rail" tier = tablet-landscape (768–1279 landscape) AND desktop (≥1280), unified: full-width
// video + full-width bottom bar (Live-desktop pattern), NO right sidebar. Desktop no longer has its own
// playback layout — it renders the SAME no-rail model as tablet-landscape, scaled up (David #86bb7phhh S31:
// "start from the tablet-landscape showing in desktop"; mirrors how Live shares tablet-landscape/desktop
// chrome). Disjoint from phone-landscape by height. The trailing `, (min-width: 1280px)` also catches a
// ≥1280 PORTRAIT window (rare) so desktop is never left unstyled. #86bb7phhh.
const WIDE_NORAIL_MQ = '(min-width: 768px) and (orientation: landscape) and (min-height: 540px), (min-width: 1280px)';
// DESKTOP (≥1280) ONLY: playback condenses the wide/no-rail model's TWO control rows (camera bar +
// timeline-card head) into ONE row above the ruler (David #86bb7phhh S31 — desktop only; tablet-landscape
// KEEPS its two rows). A subset of WIDE_NORAIL_MQ (which also covers tablet-landscape). #86bb7phhh
const DESKTOP_MQ = '(min-width: 1280px)';
// The span-restriction tiers (CSS hides 8h/24h in BOTH phone orientations): phone portrait (≤767) + phone
// landscape. NOT tablet portrait (768–1023), which keeps the full 1h/4h/8h/24h. Used to clamp a persisted
// wider span down to 4h on a phone so a user can never land on a span they can't see or re-select. #86bb7phhh
const SPAN_RESTRICTED_MQ = '(max-width: 767px) and (orientation: portrait), (orientation: landscape) and (max-height: 500px) and (max-width: 932px)';

/**
 * MCPlaybackView — synchronized multi-cam recorded playback.
 *
 * VIEW-BASED (#86bb7phhh, 2026-08-03 — supersedes the entry-form flow). Playback shows the CURRENT
 * shared Live view's cameras: there is NO entry form and no playback-local view/layout store. On
 * arrival the mount ensures a view is selected (autoSelectDefaultView) and materializes it into the grid
 * (loadViewIntoMcpb — cameras from state.view, sized to the view span up to 16), opening a recent
 * default window or RESUMING this session's committed window. Cameras + arrangement come from the view
 * (change them via the shared Views / Live Arrange); only the TIME window/playhead is playback-local.
 * The "Change" CTA re-searches the SAME cameras over a new window (DtModal — a bridge until the mobile
 * control ribbon owns time-nav inline). The grid loads PAUSED on the first in-window frame (1e snap); an
 * explicit "Go" autoplays. The committed window + playhead persist across same-session nav.
 *
 * Playback engine (1e/2a): the master `useTimelinePlayhead` drives prev/next-clip + auto-skip over the
 * UNION of all tracks' clips (so a gap on one cam another fills is not skipped); each SlotCell follows
 * the shared offset, resolves its OWN track, and shows a per-cell gap state. conf>1 fetches the 480p
 * profile (down-res). Drift correction + the 5s buffer-gate + per-cell actions + focus/drag-swap are
 * Phase 2a-3/2c/2d.
 */

// Per-cell actions (2c) live on each cell now — small Archive/Export status icons + a per-cell •••
// menu (Snapshot · Download · Archive set/unset · Export set/unset · Bookmark v2) — built in SlotCell
// from util/mcpbActions.deriveActionItems. The bulk-actions menu for conf>1 is the next chunk (task #2).

// The NVR clock ticks every second. Only these pieces read it: when MCPlaybackView read it, every tick
// re-rendered the whole grid (every tile, the timeline and the drag-and-drop layer), which kept Google
// Chrome's main thread busy for seconds while our player drew four streams, and the video stuttered (86bc8571y).
const selNvrTimestamp = (s) => s.utility.nvrTimestamp;
const selNvrTimestampShort = (s) => s.utility.nvrTimestampShort;
const PlaybackTopBar = (props) => {
  const clock = formatNvrClock(useSelector(selNvrTimestamp));
  return <MobileTopBar {...props} nvrTime={clock.time} nvrDate={clock.date} />;
};
const PlaybackRailClock = ({ daysRec, onInfo }) => {
  const clock = formatNvrClock(useSelector(selNvrTimestamp));
  return clock.time ? <RailClock date={clock.date} time={clock.time} daysRec={daysRec} onInfo={onInfo} /> : null;
};
const PlaybackRibbon = (props) => <MCPlaybackRibbon {...props} nvrTimestampShort={useSelector(selNvrTimestampShort)} />;
const PlaybackDtModal = (props) => <DtModal {...props} nvrTimestampShort={useSelector(selNvrTimestampShort)} />;

const MCPlaybackView = (props) => {
  const {
    fEview, isEview, sName, bSerial, sUserName, fExport, fPlayback, platform, machineStatus, usersOnline,
    conf, slots, sSess, selectedTimestamp, searchWindow, filters, trackEvents, eventsLoading, eventsError, playheadOffset,
    username, password, cloudAPI, sApiToken, serverSessions, currentView, currentCamView, setCamViews, viewDirty, cameras,
    getNvrTimestamp: pollNvrClock, runMcpbSearch: doSearch, logoutUser: doLogout,
    setMcpbSlotCamera: setSlotCam, refetchMcpbEvents: doRefetch,
    setMcpbConf: setConf, setMcpbSlots: setSlots, setMcpbPlayhead: savePlayhead,
    setMcpbEventFlag: flagEvent, loadViewIntoMcpb: doLoadView, autoSelectDefaultView: doAutoSelectView,
    setMcpbSpan: doSetSpan, pageMcpbWindow: doPage, setMcpbFilters: doSetFilters, getMcpbPresence: doGetPresence,
    changeGridLayout: doChangeGridLayout, camViewChange: doCamViewChange, setCameraArrangement: doSetArrangement, revertViewToPristine: doRevertViewToPristine,
    verifyPlaybackSession: doVerifySession,
  } = props;
  // System clock (date + time) — the MobileTopBar header + the landscape rail share this formatter
  // (formatNvrClock → { date, time }) so every surface shows the identical NVR clock (David #86bb7phhh).
  // This view follows only the clock's arrival and its date; the ticking clock is read by the pieces above.
  const hasNvrClock = useSelector((s) => !!selNvrTimestampShort(s));
  const nvrDay = useSelector((s) => (selNvrTimestampShort(s) || '').slice(0, 8));
  // Retention days for the rail clock's second line (David #86bb7phhh) — cohesive with Live's rail.
  const daysRec = (machineStatus && typeof machineStatus.days === 'number') ? machineStatus.days : null;
  // `eview-mode` (fEview) = theming; `mcpb-eview` (real multi-server session) = the label/timeline-width
  // concerns — cams show "Server : Cam", so the expanded-timeline label column needs more room (#86bb7phhh).
  const containerClass = `mcplayback-container ${fEview ? 'eview-mode' : ''}${isEview ? ' mcpb-eview' : ''}`;

  // Keep the SHARED arrangement (state.server.cameras) in step with a grid swap, so a rearrange is a real,
  // dirty-able, SAVEABLE view change — same as Live (#86bb7phhh, David 2026-08-05). Map the visible-set
  // slot to its index in the full array (the set offset from currentCamView) and reorder THERE, then
  // dispatch Live's setCameraArrangement (markDirty). The render still swaps `slots` too; both move together
  // — collapsing to the single source lands with the Phase-D CameraDrawer teardown, where `slots` dies.
  const swapSharedCameras = (from, to) => {
    const next = swapVisibleInFull(cameras, currentCamView, from, to);
    if (next !== cameras) doSetArrangement(next); // markDirty → viewDirty + the Views Save banner light up
  };

  // Time-nav modal (the "Change" CTA) — re-search the CURRENT view's cameras over a new window/filters.
  // A temporary bridge (reuses DtModal) until the mobile control ribbon owns time-nav inline.
  const [timeModalOpen, setTimeModalOpen] = useState(false);
  const [drawer, setDrawer] = useState({ open: false, slot: 0, replacing: false });
  // Mobile bottom-nav "More" menu + Setup (parity with Live, so mobile users can leave /mcplayback).
  const [mobileMenuOpen, setMobileMenuOpen] = useState(null); // 'menu' | null
  const toggleMobileMenu = (panel) => setMobileMenuOpen((p) => (p === panel ? null : panel));
  const closeMobileMenu = () => setMobileMenuOpen(null);
  // Setup is the ONE shared shell for every product — route to SetupView at /setup (David 2026-08-05);
  // no more standalone SetupOverlay. (The bottom nav already routes here by default; this covers the
  // sidebar + more-menu entry points.)
  const openSetup = () => { history.push('/setup'); setMobileMenuOpen(null); };
  // Desktop sidebar footer logout — mirrors MobileMoreMenu (drop the session, return to the login route).

  // Zoom-to-fill / focus (2d): the ⤢ button (or double-click) on a cell at conf>1 fills the grid with
  // it; the other cells stay MOUNTED (decoders keep running for sync) but hidden; the zoomed cell
  // switches to full-res (profile 1). Toggling the same cell, or ⤡, restores the grid. Transient UI
  // state (not persisted); reset whenever the slot set or grid size changes.
  const [zoomedSlot, setZoomedSlot] = useState(null);
  // #3 (David 2026-06-22): the FOCUSED grid cell. Tap a cell to select it, then a sidebar-cam tap places
  // that cam INTO the focused cell (swap). Transient UI state; reset when the slot set or layout changes.
  const [selectedSlot, setSelectedSlot] = useState(null);
  const handleSelectSlot = useCallback((i) => setSelectedSlot((s) => (s === i ? null : i)), []);
  const toggleZoom = useCallback((i) => { setSelectedSlot(null); setZoomedSlot((z) => (z === i ? null : i)); }, []);
  // The visible grid layout is the SHARED conf — Live and playback use the SAME selected grid
  // (state.config.conf, mapped to a number below), and the transport picker writes it back via
  // configChange, so opening playback inherits whatever layout Live was showing and either can change it
  // (#86bb7phhh). It DECOUPLES the layout from the loaded-cam count: cams beyond layoutConf stay loaded
  // (engine-safe, shown dimmed in the cam-strip); tap a pill or drag to bring one into a visible slot.
  // `conf` already IS the number; layoutConf is kept as an alias so the ~20 downstream refs read unchanged.
  const layoutConf = conf;
  // Double-tap behavior for conf-8 mirrors Live's "bubble" (VideoViewWs onDoubleClick, L-D5): the 1-big+7
  // layout PROMOTES a double-tapped SMALL tile (i>0) into the big slot (swap with position 0) instead of
  // zooming; only the BIG tile (i===0) drills to single-cam. Every other conf: double-tap → zoom-to-single.
  // An already-zoomed cell always un-zooms (toggleZoom toggles). Keeps playback ↔ Live conf-8 identical.
  const handleCellZoom = (i) => {
    if (zoomedSlot == null && layoutConf === 8 && i > 0) { swapSharedCameras(i, 0); setSlots(swapSlots(slots, i, 0)); return; }
    toggleZoom(i);
  };
  // Compact per-cell actions (David 2026-08-04): on mobile-portrait the surfaced quick-action ROW
  // (Snapshot/Download/Archive/Export) shows — when the cog "always-show" is on — ONLY on tiles big enough
  // to carry it: conf-1, conf-2, and conf-8's BIG tile (slot 0). EVERY other mobile-portrait tile collapses
  // everything into the ••• menu regardless of the toggle. Per-cell (not grid-wide) because conf-8's big
  // tile differs from its 7 small ones. Larger screens ignore this and keep the row. matchMedia behavior
  // gate (mirrors the phone-landscape transport-hide) — the styling itself stays CSS-driven.
  const [isMobilePortrait, setIsMobilePortrait] = useState(
    () => (typeof window !== 'undefined' && window.matchMedia ? window.matchMedia(MOBILE_PORTRAIT_MQ).matches : false),
  );
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    const mq = window.matchMedia(MOBILE_PORTRAIT_MQ);
    const onChange = () => setIsMobilePortrait(mq.matches);
    onChange();
    if (mq.addEventListener) mq.addEventListener('change', onChange); else mq.addListener(onChange);
    return () => { if (mq.removeEventListener) mq.removeEventListener('change', onChange); else mq.removeListener(onChange); };
  }, []);
  // Phone-landscape gate — mounts the landscape rail (ONLY there, so portrait/tests don't render its
  // reused ribbon + transport twice) + collapses small cells' action row, same as portrait (#86bb7phhh).
  const [isLandscape, setIsLandscape] = useState(
    () => (typeof window !== 'undefined' && window.matchMedia ? window.matchMedia(PHONE_LANDSCAPE_MQ).matches : false),
  );
  // Tablet-landscape mount-gate (mirrors isLandscape) — mounts the same rail model at 1024–1279 landscape.
  const [isWideNoRail, setIsWideNoRail] = useState(
    () => (typeof window !== 'undefined' && window.matchMedia ? window.matchMedia(WIDE_NORAIL_MQ).matches : false),
  );
  // Desktop-only (≥1280): the single-row control condense. A subset of isWideNoRail — desktop renders the
  // combined bar; tablet-landscape keeps the camera bar + card-head as two rows (David #86bb7phhh S31).
  const [isDesktop, setIsDesktop] = useState(
    () => (typeof window !== 'undefined' && window.matchMedia ? window.matchMedia(DESKTOP_MQ).matches : false),
  );
  // System-details sheet, opened by the rail clock's ⓘ (David #86bb7phhh — cohesive with Live).
  const [sysInfoOpen, setSysInfoOpen] = useState(false);
  const [viewsOpen, setViewsOpen] = useState(false);
  // Jump (system switch) sheet for the header crumb (#86bb7phhh S31): reuses Live's JumpControl — same
  // re-auth-into-the-picked-system flow. Gated on hasJump (via DesktopHeader/MobileTopBar), so cloud omits it.
  const [jumpOpen, setJumpOpen] = useState(false);
  // Desktop-chrome tier (>=1280 = desktop; 1280 boundary conformance 2026-09-01): Jump/Views render as the left
  // SLIDE-OUT panel (matching Live), which PUSHES the video; below 1280 (mobile/tablet incl. tablet-landscape)
  // they fall back to the bottom sheet. One matchMedia gate picks the shell — same panel-vs-sheet approach as C.
  const [isDesktopChrome, setIsDesktopChrome] = useState(
    () => (typeof window !== 'undefined' && window.matchMedia ? window.matchMedia('(min-width: 1280px)').matches : false),
  );
  // Camera picker (#86bb7phhh S32): at conf-1 the per-camera scroller is unusable on a big system, so the
  // camera bar shows a ‹ [current name] › stepper that opens this searchable list (sheet on tablet, modal on
  // desktop via .mcpb-sheet--campick). David: keep ‹ › for one-at-a-time next/prev + show the cam NAME.
  const [camPickOpen, setCamPickOpen] = useState(false);
  // Timeline EXPAND (Option A ⤢): portrait toggles the union band ↔ per-camera track rows (both mounts
  // already exist; a class on the card flips which shows). #86bb7phhh S20.
  const [timelineExpanded, setTimelineExpanded] = useState(false);
  // Hovered timeline ROW (expanded per-cam bands) → yellow highlight on BOTH the band and the matching grid
  // CELL (via SlotCell `hovered`), so it's obvious which lane maps to which camera (#86bb7phhh S33, David).
  // Holds a SLOT index (see timelineTracks slotIndex) or null. Peer-highlight only — no seek/select.
  const [hoveredRow, setHoveredRow] = useState(null);
  // "⋯ More" sheet (mobile-portrait): the secondary controls (Speed/Filters/Bulk/Options) that don't fit
  // the compact phone thumb-zone rows. The ⋯ trigger + this sheet are CSS-hidden ≥768 (tablet shows them
  // inline). #86bb7phhh.
  const [moreOpen, setMoreOpen] = useState(false);
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    const mq = window.matchMedia(PHONE_LANDSCAPE_MQ);
    const onChange = () => setIsLandscape(mq.matches);
    onChange();
    if (mq.addEventListener) mq.addEventListener('change', onChange); else mq.addListener(onChange);
    return () => { if (mq.removeEventListener) mq.removeEventListener('change', onChange); else mq.removeListener(onChange); };
  }, []);
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    const mq = window.matchMedia(WIDE_NORAIL_MQ);
    const onChange = () => setIsWideNoRail(mq.matches);
    onChange();
    if (mq.addEventListener) mq.addEventListener('change', onChange); else mq.addListener(onChange);
    return () => { if (mq.removeEventListener) mq.removeEventListener('change', onChange); else mq.removeListener(onChange); };
  }, []);
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    const mq = window.matchMedia(DESKTOP_MQ);
    const onChange = () => setIsDesktop(mq.matches);
    onChange();
    if (mq.addEventListener) mq.addEventListener('change', onChange); else mq.addListener(onChange);
    return () => { if (mq.removeEventListener) mq.removeEventListener('change', onChange); else mq.removeListener(onChange); };
  }, []);
  // >=1280 (desktop) → Jump/Views use the left slide-out panel; below (incl. tablet-landscape) → the bottom sheet.
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    const mq = window.matchMedia('(min-width: 1280px)');
    const onChange = () => setIsDesktopChrome(mq.matches);
    onChange();
    if (mq.addEventListener) mq.addEventListener('change', onChange); else mq.addListener(onChange);
    return () => { if (mq.removeEventListener) mq.removeEventListener('change', onChange); else mq.removeListener(onChange); };
  }, []);
  // Tablet-landscape renders the no-rail full-width bottom bar (its own branch); phone-landscape keeps the
  // rail. Both are gated by their own matchMedia flags above. #86bb7phhh
  // Span clamp (#86bb7phhh): a phone only offers 1h/4h (CSS hides 8h/24h in both orientations). If a wider
  // span persisted from a larger viewport — same-browser localStorage or a live resize — snap it to 4h so a
  // phone never sits on a span the user can neither see highlighted nor re-select (David's "set 24h then
  // tilt and be stuck" concern). Behavior gate (matchMedia); the layout stays CSS-driven. doSetSpan is a
  // stable connect() dispatch prop, so it's a safe dep. Fires once — clamping to 4h makes the guard false.
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    const mq = window.matchMedia(SPAN_RESTRICTED_MQ);
    const clamp = () => { if (mq.matches && windowSeconds(searchWindow) > 4 * 3600) doSetSpan(4 * 3600); };
    clamp();
    if (mq.addEventListener) mq.addEventListener('change', clamp); else mq.addListener(clamp);
    return () => { if (mq.removeEventListener) mq.removeEventListener('change', clamp); else mq.removeListener(clamp); };
  }, [searchWindow, doSetSpan]);
  // A ZOOMED cell (double-tap / ••• "View single") fills the whole viewer — it's a single-cam view like
  // conf-1, so it's never compact: the surfaced row shows there too (when always-show is on). BOTH mobile
  // orientations (portrait + phone-landscape) collapse the small-cell action row into ••• (David #86bb7phhh).
  const isCompactCell = (i) => (isMobilePortrait || isLandscape) && zoomedSlot !== i
    && !(layoutConf <= 2 || (layoutConf === 8 && i === 0));
  // Leaving playback (nav to Live / Setup / logout) silently DISCARDS unsaved view edits → the saved
  // view is what Live shows; no prompt (playback edits never require a save). A ref carries the latest
  // dirty state into the unmount closure. David 2026-08-13 #86bb7phhh.
  const viewDirtyRef = useRef(viewDirty);
  useEffect(() => { viewDirtyRef.current = viewDirty; }, [viewDirty]);
  // Keep the revert action in a ref too, so the unmount-only effect below closes over refs ONLY — that
  // makes its empty dep array genuinely correct WITHOUT an eslint-disable (doRevertViewToPristine is a
  // fresh closure each render; listing it as a dep would re-arm the unmount effect every render). #86bb7phhh.
  const revertViewRef = useRef(doRevertViewToPristine);
  useEffect(() => { revertViewRef.current = doRevertViewToPristine; });
  // Unmount-only: discard unsaved view edits when leaving playback.
  useEffect(() => () => { if (viewDirtyRef.current) revertViewRef.current(); }, []);
  // Drag-to-rearrange (conf>1): drag a cell onto another to SWAP their slots. The timeline bands follow
  // (keyed by slot index), and the cam SET is unchanged so there is no re-snap (camSetKey). Reset zoom.
  const handleSwapSlots = (from, to) => {
    swapSharedCameras(from, to);
    setSlots(swapSlots(slots, from, to));
    setZoomedSlot(null);
  };
  // Unified drag-and-drop via dnd-kit (David 2026-06-23): ALL drags share one DndContext (hoisted over the
  // whole workspace below) and the cells are the drop targets. Three sources, distinguished by the
  // draggable's `data.type`: a grid CELL (cell↔cell swap), a sidebar PILL (drag a loaded cam onto a cell),
  // or a TREE row (drag a new cam in). The floating ghost (DragOverlay) follows the cursor and the
  // source dims; routing lives in util/mcpbDrag resolveDrop (swap for cell/pill, place for tree).
  //
  // Dual sensors mirror Live's VideoViewWs (so digital zoom + drag-swap coexist): MOUSE grabs after a 6px
  // move (from the grip HANDLE only — the cell BODY's click-drag is left free for ZoomableContainer's
  // drag-select zoom); TOUCH needs a 250ms long-press so a tap / double-tap / pinch / drag-to-pan is never
  // mistaken for a swap. dnd-kit auto-selects the sensor by input type. (Replaces the single PointerSensor,
  // whose 8px mouse activation collided with the digital-zoom click-drag — David 2026-08-04.)
  const [activeDrag, setActiveDrag] = useState(null); // { cam, frame, colorVar } for the carry-the-tile ghost
  const dndSensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 6 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 250, tolerance: 8 } }),
  );
  // Capture the dragged cell's current frame so the ghost shows the ACTUAL video (like the demo's native
  // drag image), not just an identity tile. Null → dark fallback tile (cam has no frame yet / gap / off-layout).
  const captureSlotFrame = (i) => {
    // The registered handle's drawable is a <video> (local/eview/cloud-legacy) OR a <canvas> (dragon
    // WebCodecs); read whichever intrinsic-size pair is populated so both draw into the ghost.
    const el = videoEls.current[i] && videoEls.current[i].el;
    const w = el && (el.videoWidth || el.width);
    const h = el && (el.videoHeight || el.height);
    if (!el || !w) return null;
    try {
      const c = document.createElement('canvas');
      c.width = w; c.height = h;
      c.getContext('2d').drawImage(el, 0, 0);
      return c.toDataURL('image/png');
    } catch (err) { return null; }
  };
  const onDndDragStart = (e) => {
    const d = (e.active && e.active.data) ? e.active.data.current : null;
    if (!d) { setActiveDrag(null); return; }
    if (d.type === 'tree') {
      // A new cam (not a loaded slot) → identity-tile ghost, no captured frame, no slot color.
      setActiveDrag({ cam: d.cam || null, frame: null, colorVar: null });
    } else {
      // A loaded slot (cell or pill): show its cam + (if its cell is mounted) its live frame + slot color.
      const slot = d.slot;
      setActiveDrag({ cam: (slots || [])[slot] || null, frame: captureSlotFrame(slot), colorVar: 'var(--mcpb-color-accent)' });
    }
  };
  const onDndDragEnd = (e) => {
    setActiveDrag(null);
    const activeData = (e.active && e.active.data) ? e.active.data.current : null;
    const action = resolveDrop(activeData, e.over ? e.over.id : null);
    if (!action) return;
    if (action.kind === 'swap') handleSwapSlots(action.from, action.to);
    else if (action.kind === 'place') handleDropOnCell(action.slot, action.cam);
  };
  const onDndDragCancel = () => setActiveDrag(null);

  // Auto-stack the 2-up (David 2026-06-23, approach B): measure the actual VIDEO AREA (not the window) and
  // stack the two cells when that area is taller than 16:9 — below that crossover two stacked full-width
  // cells letterbox a 16:9 stream less than two side-by-side half-width cells. Only the video grid changes;
  // the sidebar/layout is untouched. Recomputed on every resize of the viewer via ResizeObserver.
  const viewerRef = useRef(null);
  const [conf2Stack, setConf2Stack] = useState(false);
  useEffect(() => {
    if (layoutConf !== 2) { setConf2Stack(false); return undefined; }
    // Tablet-landscape is wide → conf-2 is always SIDE-BY-SIDE there (matches the landscape layout glyph +
    // uses the width). Skip the aspect heuristic, which over-stacked the tall-ish no-rail viewer. #86bb7phhh
    if (isWideNoRail) { setConf2Stack(false); return undefined; }
    const el = viewerRef.current;
    if (!el) return undefined;
    const measure = () => {
      const r = el.getBoundingClientRect();
      if (r.width > 0 && r.height > 0) setConf2Stack((r.width / r.height) < (16 / 9));
    };
    measure();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [layoutConf, isWideNoRail]);
  // Timeline marker visibility ("Show on timeline" row) — visibility-only, default all-on, no refetch.
  const ALL_VISIBLE = { person: true, vehicle: true, alert: true, archived: true, export: true };
  const [vizVisibility, setVizVisibility] = useState(ALL_VISIBLE);
  const toggleViz = (k) => setVizVisibility((v) => ({ ...v, [k]: !v[k] }));
  const showAllViz = () => setVizVisibility(ALL_VISIBLE);

  const openDrawer = (slot, replacing) => setDrawer({ open: true, slot, replacing });
  const closeDrawer = () => setDrawer((d) => ({ ...d, open: false }));
  // Surgical per-cell edit in the active grid: pick → normalize → set that slot → refetch its window.
  // A pick for a slot BEYOND the current grid (the cam-strip's "+ Add camera" placeholders past conf)
  // GROWS the grid: append the cam and resize conf to fit (deriveConf 1→2→4), matching the multi-select
  // sizing — so you can build up 1→2→4 in place without round-tripping through the entry search.
  const handlePick = (rawCam) => {
    const cam = normalizeSlotCamera(rawCam, { currentSerial: bSerial });
    const cur = slots || [];
    if (drawer.slot >= cur.length) {
      const filled = cur.filter(Boolean).concat([cam]);
      const newConf = deriveConf(filled.length);
      setConf(newConf);
      setSlots(Array.from({ length: newConf }, (_, k) => filled[k] || null));
    } else {
      setSlotCam(drawer.slot, cam);
    }
    // Write the add into the SHARED arrangement (state.server.cameras) too → a real, dirty-able, SAVEABLE
    // view edit, not just a local slot change. Mirrors swapSharedCameras (the drag path). #86bb7phhh David 2026-08-13.
    const nextCameras = assignVisibleInFull(cameras, currentCamView, drawer.slot, cam);
    if (nextCameras !== cameras) doSetArrangement(nextCameras);
    doRefetch();
    closeDrawer();
  };
  const handleUnassign = (slotIndex) => {
    // Re-apply the entry sizing rule on removal: drop the cam, compact, and RE-DERIVE conf so the grid
    // follows the cam count (4→…→1 cam = single, not a 4-up with empty cells — David 2026-06-22).
    const { conf: newConf, slots: resized } = removeAndResize(slots, slotIndex);
    setConf(newConf);
    setSlots(resized);
    // Remove from the SHARED arrangement too (compact) → dirty-able/saveable, and it reverts on leave. #86bb7phhh.
    const nextCameras = removeVisibleInFull(cameras, currentCamView, slotIndex);
    if (nextCameras !== cameras) doSetArrangement(nextCameras);
    doRefetch();
  };
  // Drop a NEW cam (from the tree) onto a specific cell: set THAT slot (fill an empty cell or replace an
  // occupied one), then refetch its window. Invoked by the unified dnd-kit drop routing (onDndDragEnd →
  // resolveDrop 'place'); a changed cam SET re-snaps via the existing camSetKey effect.
  const handleDropOnCell = (slotIndex, rawCam) => {
    const cam = normalizeSlotCamera(rawCam, { currentSerial: bSerial });
    if (!cam) return;
    setSlotCam(slotIndex, cam);
    // Tree→cell drop is an add too — write the shared arrangement so it dirties + saves. #86bb7phhh.
    const nextCameras = assignVisibleInFull(cameras, currentCamView, slotIndex, cam);
    if (nextCameras !== cameras) doSetArrangement(nextCameras);
    doRefetch();
  };

  // Saved layouts (2e) — keyed by {serial, user}; surfaced in the camera drawer (load · save · delete).
  // pushToast is defined below in the action-feedback block; these handlers only call it on user action.
  const { layouts: savedLayouts, saveLayout, renameLayout, replaceLayout, deleteLayout, atCap: atLayoutCap } = useSavedLayouts({ serial: bSerial, user: sUserName });
  const handleSaveLayout = (name) => {
    const filled = (slots || []).filter(Boolean);
    if (!filled.length) { pushToast({ message: 'Add a camera before saving a layout', tone: 'error' }); return; }
    const res = saveLayout(name, filled);
    if (!res.ok) pushToast({ message: 'Maximum 50 layouts — delete one first', tone: 'error' });
    else pushToast({ message: res.warn ? 'Layout saved — that is a lot; consider consolidating' : 'Layout saved', tone: 'success' });
  };
  const handleLoadLayout = (layout) => {
    const cams = (layout && layout.slots) || [];
    if (!cams.length) return;
    const newConf = deriveConf(cams.length);
    setConf(newConf);
    setSlots(Array.from({ length: newConf }, (_, i) => cams[i] || null));
    savePlayhead(0);
    doRefetch();      // re-fetch the committed window with the layout's cams
    closeDrawer();
  };
  const handleDeleteLayout = (id) => deleteLayout(id);
  const handleRenameLayout = (id, name) => renameLayout(id, name);
  // Replace = overwrite a saved layout's cam set with the cameras currently loaded (id/name kept).
  const handleReplaceLayout = (id) => {
    const filled = (slots || []).filter(Boolean);
    if (!filled.length) { pushToast({ message: 'Add a camera before replacing a layout', tone: 'error' }); return; }
    replaceLayout(id, filled);
    pushToast({ message: 'Layout updated', tone: 'success' });
  };
  // The saved layout (if any) whose cam SET matches the current slots — drives the tablet-landscape /
  // desktop sidebar's LOADED-LAYOUT indicator (null → "Custom"). Order-independent (a reorder is the
  // same layout). The "View all" / "Save current" sidebar shortcuts open the drawer's layouts section.

  // Tick the NVR clock (shared header) once per second — mirrors Live/Playback. Local
  // NVR product only: cloud has no NVR to poll (that JSON-RPC would hit a nonexistent
  // local backend), so gate it on the product profile like LiveView does.
  useEffect(() => {
    if (!sSess || !getProductConfig().pollsNvrClock) return undefined;
    const id = setInterval(() => pollNvrClock(sSess), 1000);
    return () => clearInterval(id);
  }, [pollNvrClock, sSess]);

  // ── View-based state: the grid shows the CURRENT shared view's cameras (no entry form) ──────────────
  const hasRunnableSearch = !!selectedTimestamp && (slots || []).some(Boolean);

  // Change time: re-search the CURRENT view's cameras at a new anchor — the cameras come from the shared
  // view (never picked here). autoPlay on the explicit "Go", like a fresh search. The mobile "Go to time"
  // picker commits DATE + START TIME only ({ anchorTimestamp }); the window WIDTH is the ribbon span and
  // filters live on the ribbon, so fall back to the COMMITTED window/filters when the payload omits them
  // (one source). DtModal (full mode) still passes an explicit window + filters, which take precedence.
  const handleChangeTime = ({ anchorTimestamp, window: win, filters: f }) => {
    savePlayhead(0);
    autoPlayRef.current = true;
    placedForRef.current = ''; // an explicit "Go" re-places the marker, even onto the same window
    doSearch({
      anchorTimestamp,
      window: win !== undefined ? win : searchWindow,
      filters: f !== undefined ? f : filters,
    });
    setTimeModalOpen(false);
  };

  // ── Playback engine (1e) ────────────────────────────────────────────
  const windowStartTs = selectedTimestamp || '';
  const windowLenSec = windowSeconds(searchWindow);
  // "Filter by" (committed filters) narrows the visible clip set CLIENT-SIDE before anything consumes it
  // — the timeline, the union playhead clock (prev/next + auto-skip), and each cell all see only the
  // matching clips. All filters off = the full set (mcpbFilter). Memoized on the raw events + filters.
  const visibleTrackEvents = useMemo(() => filterTrackEvents(trackEvents, filters), [trackEvents, filters]);

  // Over-the-timeline search-result notice (no silent empty): once a committed search has settled, say
  // WHY the lane is empty — a hard failure vs a legitimately empty result. Lives on the timeline (where
  // the eye goes after a search), not a corner toast. Raw event count (a 0-event search), not filtered.
  // Counted over the cameras ON SCREEN: trackEvents also holds the view's other sets once they are
  // prefetched (86bc8571y), and those must not hide an empty result for this set.
  const mcpbEventCount = useMemo(
    () => (slots || []).reduce((n, c) => {
      const list = c ? trackEvents[`${c.bSerial}_${c.bCamera}`] : null;
      return n + (list ? list.length : 0);
    }, 0),
    [trackEvents, slots],
  );
  const timelineNotice = (selectedTimestamp && !eventsLoading)
    ? (eventsError
      ? { message: 'Could not load events — check the connection.', tone: 'error' }
      : (mcpbEventCount === 0 ? { message: 'No recorded events in this time range.', tone: 'empty' } : null))
    : null;
  // 2a: the master clock drives prev/next-clip + auto-skip over the UNION of ALL tracks' clips (so a gap
  // on one cam that another fills is not skipped); each SlotCell resolves its OWN track for per-cell gap
  // rendering, and the Timeline draws one band per track (2a-4).
  const unionClips = unionTrackClips(slots, visibleTrackEvents);
  // Whole-window-empty notice (SO 38150, dview origin 1d49f83b9): once the load has settled without an
  // error and NO camera has a clip that passes the filters, say so over the grid and hold the clock
  // (emptyWindow below) instead of running the marker over dark tiles. Per-tile gaps stay text-free
  // (David 2026-08-04); this is only the every-camera-empty case. Wording matches dview.
  // A page reload strips trackEvents and starts with eventsLoading false; the mount refetch below only
  // raises eventsLoading after the first paint. Until it has, the window is not "loaded", so the notice
  // can't flash before the load even starts.
  const [mountLoadPending, setMountLoadPending] = useState(
    () => hasRunnableSearch && !eventsLoading && Object.keys(trackEvents || {}).length === 0,
  );
  useEffect(() => { if (eventsLoading) setMountLoadPending(false); }, [eventsLoading]);
  const emptyWindow = hasRunnableSearch && !mountLoadPending && !eventsLoading && !eventsError && unionClips.length === 0;
  const anyFilterActive = Object.keys(filters || {}).some((k) => filters[k]);
  const emptyWindowNotice = emptyWindow
    ? (anyFilterActive ? 'No video matches the filters in this time range' : 'No recorded video in this time range')
    : null;
  const slotsKey = (slots || []).map((c) => (c ? `${c.bSerial}_${c.bCamera}` : '_')).join(',');
  // Order-INDEPENDENT key (the SET of cams, not their order) — drives the first-frame snap so that
  // drag-REORDERING the same cams doesn't re-snap and jump the playhead (only a changed cam set does).
  const camSetKey = (slots || []).map((c) => (c ? `${c.bSerial}_${c.bCamera}` : '_')).slice().sort().join(',');
  // Timeline bands: one per LOADED slot — the timeline shows EVERY selected cam for cross-cam awareness
  // even when the layout views fewer (David 2026-06-19); bands for cams not in the current view are
  // dimmed. 1 loaded cam → one band (1f). Bands render the single product accent (--mcpb-color-accent,
  // green eView / blue dView); the per-cam palette was retired (#86bb7phhh S33, David: "too fruity").
  const timelineTracks = (slots || []).map((cam, i) => (
    // Skip empty-slot placeholders (fEnable:false) — no timeline row for them (David: show nothing on empties).
    (cam && cam.fEnable !== false) ? {
      clips: (visibleTrackEvents && visibleTrackEvents[`${cam.bSerial}_${cam.bCamera}`]) || [],
      // Carry the SLOT index so a hovered band highlights the matching grid CELL. Bands are .filter()ed, so
      // the post-filter array index would drift from the slot/cell index whenever a view has empty slots.
      slotIndex: i,
      dimmed: i >= layoutConf,
      // Per-row label on the expanded multi timeline (#86bb7phhh S20/S32): the cam NUMBER as its own badge
      // (David — show it on the row) + the descriptive name (eView "NVR : Name", local just the name since
      // the number is now the badge, not the "id - " prefix cameraDisplayLabel would add).
      num: String(cam.bID != null ? cam.bID : (cam.bCamera != null ? cam.bCamera : '')),
      name: (isEview && cam.bSerial) ? `${cam.sServerName || 'NVR'} : ${cam.sName || ''}` : (cam.sName || ''),
    } : null
  )).filter(Boolean);

  // Buffer-gate (2a-3): each SlotCell reports its {hasClip, isBuffering} into stallRef (a ref — no
  // re-render churn); the master holds the shared clock while any genuine cell underruns (up to the 5s
  // ceiling inside useTimelinePlayhead). Reset the map when the slot set changes so stale entries can't
  // wedge the gate. shouldHold is stable; it reads the live ref each tick.
  const stallRef = useRef({});
  // Set once the playhead hook exists (below): a cell that finishes loading re-checks the gate at once
  // rather than waiting for the next 1 Hz tick (86bc8571y).
  const recheckHoldRef = useRef(null);
  const onStall = useCallback((i, partial) => {
    const prev = stallRef.current[i];
    stallRef.current[i] = { ...prev, ...partial };
    if (cellBecameReady(prev, partial) && recheckHoldRef.current) recheckHoldRef.current();
  }, []);
  // Per-clip cloud cache progress (bID → percent) for the timeline bars: cells report their active
  // clip's pull 0→100 as it caches, so each cam's band shades in (bright = not cached → dark fill grows
  // → cached) — the dview MCTimelineBar setClipCloudCache behavior. Already-cached clips come straight
  // from the events' fCached; this map carries the in-flight + newly-cached ones. Reset on a new search.
  const [cacheByClip, setCacheByClip] = useState({});
  const onCacheProgress = useCallback((bID, pct) => {
    if (bID == null) return;
    setCacheByClip((m) => (m[bID] === pct ? m : { ...m, [bID]: pct }));
  }, []);
  useEffect(() => { setCacheByClip({}); }, [windowStartTs, camSetKey]);
  // While zoomed (2d), only the VISIBLE (zoomed) cell may hold the clock — a hidden cell that reports
  // buffering must not freeze the one on screen. Read zoomedSlot via a ref so shouldHold stays stable.
  const zoomedSlotRef = useRef(zoomedSlot);
  zoomedSlotRef.current = zoomedSlot;
  const shouldHold = useCallback(() => {
    const z = zoomedSlotRef.current;
    if (z != null) return slotCanStall(stallRef.current[z]);
    return anyCellCanStall(Object.values(stallRef.current));
  }, []);
  // Progress-aware hold (ADR-030): is a HELD (genuinely-buffering) cell still LOADING — its <video>
  // buffer grown since the last tick? If so the gate holds the timeline until it's actually ready,
  // instead of marching ahead after the 5s ceiling (which desyncs a slow-but-recovering stream and
  // causes the buffer/re-seek churn on a full-res-only NVR). Mirrors shouldHold's scope (zoomed → just
  // that cell, else all). bufEndRef snapshots each held cell's last buffered end for the tick-to-tick
  // comparison; a stream with no data arriving never "progresses" → it still trips the ceiling.
  const bufEndRef = useRef({});
  const holdProgressing = useCallback(() => {
    const z = zoomedSlotRef.current;
    const ids = z != null ? [z] : Object.keys(stallRef.current).map(Number);
    const held = [];
    ids.forEach((i) => {
      if (!slotCanStall(stallRef.current[i])) return; // only cells that are genuinely buffering
      // A cloud clip being pulled to cache has no <video> src yet (so no buffer to "grow") but is
      // legitimately loading (caching). Otherwise a held cell is "progressing" only if its <video>
      // buffer grew this tick; a flat buffer with no data arriving is a WEDGED stream.
      const caching = !!(stallRef.current[i] && stallRef.current[i].caching);
      const v = videoEls.current[i] && videoEls.current[i].el;
      const end = (v && v.buffered && v.buffered.length) ? v.buffered.end(v.buffered.length - 1) : 0;
      const grew = end > (bufEndRef.current[i] || 0) + 0.1;
      bufEndRef.current[i] = end;
      held.push({ caching, grew });
    });
    // Hold indefinitely only while EVERY held cell is still loading; if ANY is wedged, return false so
    // the 5s ceiling accrues and the grid recovers — a single caching sibling must NOT mask a dead cell
    // and freeze the whole grid until its (~30s) cache timeout.
    return heldCellsAllProgressing(held);
  }, []);
  // Reset the gate + exit zoom when the slot set OR the visible layout changes (cells mount/unmount, so
  // stale stall entries from no-longer-rendered cells can't wedge the master clock).
  // Reset bufEndRef alongside stallRef: otherwise the first buffering tick after a slot/layout swap
  // compares the NEW cam's buffered.end() against the PREVIOUS cam's stale value (#86baz05kd).
  // A LAYOUT effect (86bc8571y): it runs before the cells' passive effects in the same commit, so a new
  // cell's buffering report (a set swap whose events were prefetched loads its clip in that very render)
  // lands in the fresh map instead of being wiped by the reset after it.
  useLayoutEffect(() => { stallRef.current = {}; bufEndRef.current = {}; }, [slotsKey, layoutConf]);
  useEffect(() => { setZoomedSlot(null); setSelectedSlot(null); }, [slotsKey, layoutConf]);

  // Auto-skip-gaps toggle (3a-4) — drives useTimelinePlayhead.autoSkip. Default ON (matches the prior
  // hardcoded behavior); a reviewer can turn it OFF to sit through dead air. Persisted to localStorage.
  const [autoSkip, setAutoSkip] = useState(() => {
    try { return localStorage.getItem('mcpbAutoSkip') !== '0'; } catch (e) { return true; }
  });
  const toggleAutoSkip = () => setAutoSkip((on) => {
    const next = !on;
    try { localStorage.setItem('mcpbAutoSkip', next ? '1' : '0'); } catch (e) { /* ignore */ }
    return next;
  });

  // Cog "Camera Info overlay" (David 2026-06-21) — the global on-video VIDEO/STREAM-info toggle (the
  // desktop DstreamView "Toggle Info" equivalent; §15 #22), persisted (§15 Q16). Off by default (a
  // diagnostic overlay, opt-in — desktop drawFPS=false parity). Threaded to the cog (Transport) and,
  // when on, to every populated SlotCell which draws it top-left.
  const [videoInfo, setVideoInfo] = useState(getVideoInfoPref);
  const toggleVideoInfo = () => setVideoInfo((on) => setVideoInfoPref(!on));
  // #16 "Always-show quick actions" (David 2026-06-23): when on, each cell's Snapshot/Download/Archive/
  // Export row is pinned visible (set as data-quick-actions on the container); off → it reveals on hover
  // (mouse) / single-tap (touch). Default ON. Persisted, threaded to the cog + the cells' menu de-dup.
  const [quickActionsAlways, setQuickActionsAlways] = useState(getQuickActionsPref);
  const toggleQuickActions = () => setQuickActionsAlways((on) => setQuickActionsPref(!on));

  const { offsetSec, playing, speed, holding, play, togglePlay, prevClip, nextClip, setSpeed, seek, recheckHold } =
    useTimelinePlayhead({ windowLenSec, clips: unionClips, windowStartTs, autoSkip, emptyWindow, initialOffset: playheadOffset, shouldHold, holdProgressing });
  recheckHoldRef.current = recheckHold;

  // Transport is now an always-visible in-flow bar (David #86bb7phhh): in landscape it shares the sets row
  // (streamlined — playback + speed), and its conf/bulk/options move to the rail. No overlay, no auto-hide.

  // Set/bank paging by SWIPE (mobile) — mirrors Live's grid swipe (LiveView handleTouchStart/End). A quick
  // horizontal drag pages to the prev/next ENABLED set (setCamViews — the same real banks the strip shows,
  // skipping empties) via camViewChange, which the currentCamView effect turns into a slots reload. The 50px
  // threshold + the dnd long-press keep tap / double-tap-zoom / cell-drag untouched; off while a cell is
  // digitally zoomed (a horizontal drag pans the zoom then). #86bb7phhh.
  const swipeStartXRef = useRef(null);
  const onGridTouchStart = (e) => { swipeStartXRef.current = (e.touches && e.touches[0]) ? e.touches[0].clientX : null; };
  const onGridTouchEnd = (e) => {
    const startX = swipeStartXRef.current;
    swipeStartXRef.current = null;
    if (startX == null || zoomedSlot != null) return;
    const t = e.changedTouches && e.changedTouches[0];
    if (!t) return;
    const dx = t.clientX - startX;
    if (Math.abs(dx) < 50) return; // below the swipe threshold -> leave taps / drags alone
    const sets = setCamViews || [];
    const i = sets.indexOf(currentCamView);
    if (i < 0) return;
    if (dx < 0 && i < sets.length - 1) doCamViewChange(sets[i + 1]); // swipe left -> next enabled set
    else if (dx > 0 && i > 0) doCamViewChange(sets[i - 1]); // swipe right -> prev enabled set
  };

  // Track the live offset + persist it on unmount so a same-session nav away + back restores the moment.
  const offsetRef = useRef(offsetSec);
  offsetRef.current = offsetSec;
  const saveRef = useRef(savePlayhead);
  saveRef.current = savePlayhead;
  useEffect(() => () => { saveRef.current(offsetRef.current); }, []);

  // Persist the live offset PERIODICALLY while playing, not only on unmount: a hard page REFRESH tears
  // the JS context down without running React cleanup, so the unmount save never fires. A 3s tick keeps
  // the persisted playheadOffset within a few seconds of the watched moment, so a refresh restores you
  // near where you were (paired with the persisted search/cameras). Offset lives in a ref (no per-second
  // re-render); this only dispatches on the coarse tick.
  const doRefetchRef = useRef(doRefetch);
  doRefetchRef.current = doRefetch;
  useEffect(() => {
    if (!playing) return undefined;
    const id = setInterval(() => saveRef.current(offsetRef.current), 3000);
    return () => clearInterval(id);
  }, [playing]);

  // After a page refresh the committed search (cameras/window/filters/offset) is rehydrated from
  // persistence, but the fetched events are NOT (stripped by the store mcplaybackTransform — stale +
  // large). Re-fetch them ONCE on mount so the restored grid repopulates via the rehydrated backend
  // instead of showing an empty timeline. Mount-only (a ref guard) so in-session searches — which fetch
  // through runMcpbSearch — never double-fetch, and a legitimately-empty result never loops.
  const didMountRefetchRef = useRef(false);
  const aliveRef = useRef(true);
  useEffect(() => () => { aliveRef.current = false; }, []);
  useEffect(() => {
    if (didMountRefetchRef.current) return;
    didMountRefetchRef.current = true;
    const haveEvents = Object.keys(trackEvents || {}).length > 0;
    if (hasRunnableSearch && !eventsLoading && !haveEvents) {
      // Settle the empty-window gate even when the refetch never raises eventsLoading (nothing to query).
      Promise.resolve(doRefetchRef.current()).finally(() => { if (aliveRef.current) setMountLoadPending(false); });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // View-based entry (#86bb7phhh): playback shows the CURRENT shared Live view's cameras — no entry form.
  // On arrival, materialize the current view and open a default window (loadViewIntoMcpb). Guarded
  // (didInitViewRef) so the once-per-second NVR-clock tick can't refetch.
  //
  // This ALWAYS runs now, including on a rehydrated/in-session search — see the comment inside. On a
  // same-session return from Live, trackEvents is still in redux, so the mount-refetch above sees
  // haveEvents and stays out of the way. On a full page reload persist strips trackEvents, so both fire
  // and issue the same query; loadMultiCamEvents' generation token drops the loser, and one extra RPC on
  // a reload is a cheaper trade than re-introducing the stale-view bug this fix removed.
  const didAutoSelectRef = useRef(false);
  const didInitViewRef = useRef(false);
  const lastLoadedViewRef = useRef(null);
  const lastCamViewRef = useRef(currentCamView);
  useEffect(() => {
    if (didInitViewRef.current) return;
    if (!hasNvrClock) return; // wait for the NVR clock (default window is [now - 1h, now])
    if (!currentView) { // no view yet -> pick the default, then this effect re-runs with it set
      if (!didAutoSelectRef.current) { didAutoSelectRef.current = true; doAutoSelectView(); }
      return;
    }
    didInitViewRef.current = true;
    lastLoadedViewRef.current = currentView;
    lastCamViewRef.current = currentCamView;
    // ALWAYS re-materialize from the current view on mount — do NOT skip on a retained search
    // (`hasRunnableSearch`). Playback slots are purely view-derived (nothing else ever writes them),
    // and mcplayback state survives route navigation, so skipping left the PREVIOUS view's cameras on
    // screen under the NEW view's name after Playback -> Live -> switch view -> Playback (#86bb7phhh,
    // cs2427 2026-08-17). Re-loading is cheap and non-destructive: loadViewIntoMcpb resumes the
    // committed window and only refetches that set's events, so a plain refresh still lands on the
    // same grid at the same position.
    doLoadView();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasNvrClock, currentView]);

  // In-session: the user switched the shared view -> reload playback from the new view. Re-sync the set
  // ref too (selectView also moves currentCamView) so the set effect below doesn't double-load.
  useEffect(() => {
    if (!didInitViewRef.current) return;
    if (!currentView || currentView === lastLoadedViewRef.current) return;
    lastLoadedViewRef.current = currentView;
    lastCamViewRef.current = currentCamView;
    saveRef.current(offsetRef.current); // keep the watched moment across the reload (see the set effect)
    doLoadView();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentView]);

  // In-session: the user paged the SET (CamButtonsView / swipe -> camViewChange, or a layout change via
  // changeGridLayout, which moves conf + set together). Reload from the new set — loadViewIntoMcpb
  // recomputes the slots from currentCamView and, since a window is committed, refetches that set's events
  // over the SAME window (no new window). Bounded to the visible set (≤ conf cams), so it's fast (#86bb7phhh).
  useEffect(() => {
    if (!didInitViewRef.current) return;
    if (currentCamView === lastCamViewRef.current) return;
    lastCamViewRef.current = currentCamView;
    // Keep the moment being watched (86bc8571y). The snap after the reload seeks to the STORE offset,
    // which is only written while playing (every 3 s) or on unmount; after a paused jump it was stale,
    // so a set swap snapped the grid back to the start of the window.
    saveRef.current(offsetRef.current);
    doLoadView();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentCamView]);

  // Snap to the first in-window playable frame on a fresh (window, slot) — once per key, never fights
  // the user. A restored offset (>0 at mount) holds for its initial key (skip the snap that once).
  const snappedRef = useRef('');
  const restoredRef = useRef((Number(playheadOffset) || 0) > 0);
  // Autoplay after an explicit time-change "Go" — the user asked to watch a new window, so start playing
  // once the events come back + the playhead snaps to the first frame, rather than sitting paused. Set in
  // handleChangeTime, consumed once by the snap effect below. Arriving on a view (mount load) does NOT
  // set it, so a view opens PAUSED on its first frame.
  const autoPlayRef = useRef(false);
  // Active "Filter by" signature — changing it narrows the playable set, so the playhead re-snaps to the
  // first matching clip (else it can sit in a now-filtered-out gap after you apply a filter).
  const activeFilterKey = Object.keys(filters || {}).filter((k) => filters[k]).sort().join(',');
  // The (window, cam set) the playhead was last placed for. A filter that empties a window the user is
  // already watching must NOT move the marker (only the clock holds); a new window or cam set does.
  const placedForRef = useRef('');
  useEffect(() => {
    if (!windowStartTs) return;
    const placeKey = `${windowStartTs}|${camSetKey}`;
    if (!unionClips.length) {
      // An empty window has no first frame to snap to: park the held marker at the committed moment (0 =
      // the window start for a fresh search) rather than wherever the previous window left it, which could
      // read as a finished playback. Once per (window, cam set), so a filter toggle or a click on the
      // timeline inside an empty window is never pulled back. An explicit "Go" that found nothing does
      // not autoplay later (SO 38150). A restored offset is the moment to restore: keep it, and consume the
      // restore so the next real load snaps normally.
      if (!emptyWindow) return;
      autoPlayRef.current = false;
      if (placedForRef.current === placeKey) return;
      placedForRef.current = placeKey;
      if (restoredRef.current) { restoredRef.current = false; return; }
      seek(Number(playheadOffset) || 0);
      return;
    }
    placedForRef.current = placeKey;
    const key = `${windowStartTs}|${camSetKey}|${activeFilterKey}`;
    if (snappedRef.current === key) return;
    snappedRef.current = key;
    if (restoredRef.current) { restoredRef.current = false; return; }
    // A span-zoom / page re-anchors the store playhead to the moment being watched (setMcpbSpan writes
    // playheadOffset>0 to keep the same wall-clock instant across the re-window); honor that. A fresh
    // search / page-to-start commits offset 0 -> snap to the first playable frame instead.
    const committedOffset = Number(playheadOffset) || 0;
    seek(committedOffset > 0 ? committedOffset : firstPlayableOffset(unionClips, windowStartTs));
    if (autoPlayRef.current) { autoPlayRef.current = false; play(); } // autoplay a fresh search
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [windowStartTs, camSetKey, activeFilterKey, unionClips.length, emptyWindow]);

  // Transport readout: the current playhead wall-clock time only (David 2026-06-19 — the window-end is
  // redundant with the timeline ticks; dropping it frees room for the bulk menu in the transport).
  const playheadTs = windowStartTs ? addSeconds(windowStartTs, offsetSec) : '';
  const elapsed = playheadTs ? chipTime(playheadTs) : '';

  // ── Action feedback surface (2c) — per-cell actions push toasts here; download progress shows here too.
  // Each SlotCell owns its own ••• actions (snapshot/download/archive/export) using its clip + video ref;
  // the View hands down the shared primitives (flag thunk, toast push, clip-download starter, export right).
  const { toasts, push: pushToast, dismiss: dismissToast } = useToasts();
  // 2 at a time, not 5: every MCPlayback download carries the server OSD burn (&overlay=1), and each
  // burn holds one of pbserver's FOUR shared overlay/transcode semaphore slots for the whole render.
  // Draining a bulk download two at a time leaves headroom for live playback's profile-2 transcodes on
  // the same box. Anything over the cap queues rather than erroring. #86bb7phhh
  const { downloads, startDownload, cancelDownload } = useClipDownload({ maxConcurrent: 2 });

  // Action toasts + in-flight clip-download progress, rendered as one stack (download rows auto-expire,
  // so they are not manually dismissable).
  const downloadToasts = (downloads || []).map((d) => ({
    id: `dl-${d.id}`,
    dismissable: d.state === 'downloading', // in-flight → the ✕ CANCELS it; done/error auto-expire (no ✕)
    tone: d.state === 'downloading' ? 'info' : d.state === 'done' ? 'success' : 'error',
    message: d.state === 'downloading'
      ? `Downloading ${d.filename}…${typeof d.progress === 'number' ? ` ${d.progress}%` : ''}`
      : d.state === 'done' ? `Downloaded ${d.filename}`
        : (d.error || 'Download failed'),
    // Progress bar (only while downloading): a number 0–100 → determinate; null → indeterminate
    // ("rendering"/no Content-Length). undefined on done/error → no bar.
    progress: d.state === 'downloading' ? (typeof d.progress === 'number' ? d.progress : null) : undefined,
  }));
  const allToasts = toasts.concat(downloadToasts);

  // ── Bulk actions (2c, conf>1) — "apply to all visible cams". SlotCells register a per-slot media
  // handle `{ el, src }` here — `el` is the drawable frame source (<video> for local/eview/cloud-legacy,
  // the active <canvas> for dragon WebCodecs) and `src` is the clip download URL — so Snapshot/Download-all
  // can reach each cell regardless of product type; Archive/Export-all resolve each cell's clip under the
  // shared playhead. Default scope = all filled cells (the "adjust which cams" picker is v2).
  const videoEls = useRef({});
  const registerVideo = useCallback((i, handle) => {
    if (handle) videoEls.current[i] = handle; else delete videoEls.current[i];
  }, []);
  const visibleSlots = (slots || []).slice(0, layoutConf).map((cam, i) => ({ cam, i })).filter((x) => x.cam);
  // Expand (union timeline -> per-camera rows) is only meaningful with >1 visible cam; a single cam
  // has exactly one row, so there's nothing to expand (David 2026-08-10). Hide the toggle + never let
  // the card sit in an expanded state at 1 cam.
  const canExpandTimeline = visibleSlots.length > 1;
  const activeClipForSlot = (cam) => {
    if (!cam || !windowStartTs) return null;
    const clips = (visibleTrackEvents && visibleTrackEvents[`${cam.bSerial}_${cam.bCamera}`]) || [];
    const a = clipAtOffset(clips, offsetSec, windowStartTs);
    return a ? a.clip : null;
  };
  // Set the flag (value=true) on every visible cell's current clip; one toast + an Undo that reverts
  // exactly the clips this action changed.
  const bulkFlag = async (flag, verb) => {
    const hits = [];
    visibleSlots.forEach(({ cam }) => {
      const ev = activeClipForSlot(cam);
      if (ev) hits.push({ bID: ev.bID, serial: cam.bSerial, deviceId: cam.sDeviceID });
    });
    if (!hits.length) { pushToast({ message: 'No clips at this time', tone: 'error' }); return; }
    // Fire every flag RPC and TRACK its result — don't report an unconditional green "success" when the
    // RPCs fail (server down) and the optimistic flags all silently revert. flagEvent resolves with {ok};
    // failure = res present with a falsy ok, or a reject. Toast success / partial / all-failed honestly,
    // and scope the Undo to the clips that actually changed. #86baz05kd
    const oks = await Promise.all(hits.map((h) =>
      Promise.resolve(flagEvent({ bID: h.bID, flag, value: true, serial: h.serial, deviceId: h.deviceId }))
        .then((res) => !(res && !res.ok))
        .catch(() => false)));
    const okHits = hits.filter((_, i) => oks[i]);
    if (!okHits.length) { pushToast({ message: 'Couldn\'t reach the server — no changes saved', tone: 'error' }); return; }
    const failed = hits.length - okHits.length;
    pushToast({
      message: failed
        ? `${verb} ${okHits.length} of ${hits.length} cams (${failed} failed)`
        : `${verb} ${okHits.length} cam${okHits.length > 1 ? 's' : ''}`,
      tone: 'success',
      undoLabel: 'Undo',
      onUndo: () => okHits.forEach((h) => flagEvent({ bID: h.bID, flag, value: false, serial: h.serial, deviceId: h.deviceId })),
    });
  };
  const handleBulk = (action) => {
    if (action === 'snap-all') {
      // ONE composite image of the on-screen grid (not N separate downloads) — cells row-major with empty
      // slots black, matching the layout (conf-1 = 1 col, conf-2/conf-4 = 2 cols). #86baz05kd
      const cols = layoutConf <= 1 ? 1 : 2;
      const cells = (slots || []).slice(0, layoutConf).map((cam, i) => {
        const h = videoEls.current[i];
        return (cam && h && h.el) ? { source: h.el, camName: cam.sName, timestamp: elapsed } : null;
      });
      const hasFrame = cells.some((c) => c && c.source && (c.source.videoWidth || c.source.width));
      const stamp = String(elapsed || '').replace(/[/:]/g, '').replace(/\s+/g, '_') || Date.now();
      const ok = downloadComposite(cells, { cols, filename: `mcplayback_grid_${stamp}.png` });
      if (ok) pushToast({ message: 'Grid snapshot saved', tone: 'success' });
      else if (hasFrame) pushToast({ message: "Couldn't capture the grid — cross-origin (eView) cameras can't be composited", tone: 'error' });
      else pushToast({ message: 'No frames to capture yet', tone: 'error' });
    } else if (action === 'dl-all') {
      let n = 0;
      visibleSlots.forEach(({ cam, i }) => { const h = videoEls.current[i]; const src = h && (h.src || (h.el && h.el.src)); if (src) { startDownload(src, { camName: cam.sName, timestamp: elapsed }); n += 1; } });
      if (!n) pushToast({ message: 'No clips to download', tone: 'error' });
    } else if (action === 'archive-all') {
      bulkFlag(ARCHIVE_FLAG, 'Archived');
    } else if (action === 'export-all') {
      if (!fExport) return;
      bulkFlag(EXPORT_FLAG, 'Flagged for export');
    }
  };
  const bulkItems = deriveBulkItems({ visibleCount: visibleSlots.length, canExport: !!fExport, isWebCodecs: !!cloudAPI })
    .map((it) => ({ ...it, onClick: () => handleBulk(it.action) }));

  // conf>1 fetches the 480p profile (down-res) to cut multi-cam decode load (Java parity); conf=1 = native.
  const streamCtx = { cloudAPI, sApiToken, fEview, username, password, profile: layoutConf > 1 ? 2 : 1 };

  // The portrait control ribbon (Views · Filters · span/zoom · date-time+search chip → WhenSheet). Rendered
  // in the stage for portrait AND — via this same factory — in the mobile-LANDSCAPE rail, so landscape
  // REUSES portrait's exact controls + shared sheets instead of a bespoke set. A factory (not a shared
  // element) so each mount point owns its own sheet state; only one is visible per breakpoint via CSS.
  const renderRibbon = (parts) => (
    <PlaybackRibbon
      parts={parts}
      selectedTimestamp={selectedTimestamp}
      searchWindow={searchWindow}
      filters={filters}
      playheadTs={playheadTs}
      currentViewName={currentView}
      viewDirty={viewDirty}
      onSearch={handleChangeTime}
      onSetSpan={doSetSpan}
      onPage={doPage}
      onSetFilters={doSetFilters}
      onRequestPresence={(year, month0) => doGetPresence({ year, month0 })}
    />
  );

  // The playback transport — a factory so each mount owns its own sheet state (sheets portal to
  // .mcplayback-container). `parts` selects which clusters render (Option A portrait mounts subsets:
  // 'playback' in the timeline card, 'layout'/'options' on the banks row); no args = the whole bar (the
  // landscape rail). #86bb7phhh S20.
  const renderTransport = (parts, className, opts = {}) => (
    <Transport
      parts={parts}
      className={className}
      playing={playing}
      speed={speed}
      elapsed={elapsed}
      inline
      autoSkip={autoSkip}
      onToggleAutoSkip={toggleAutoSkip}
      videoInfo={videoInfo}
      onToggleVideoInfo={toggleVideoInfo}
      quickActionsAlways={quickActionsAlways}
      onToggleQuickActions={toggleQuickActions}
      onPlayPause={togglePlay}
      onPrev={prevClip}
      onNext={nextClip}
      onSkipBack={() => seek(offsetSec - 10)}
      onSkipFwd={() => seek(offsetSec + 10)}
      onSpeed={setSpeed}
      layout={layoutConf}
      onLayout={(n) => doChangeGridLayout(`conf-${n}`)}
      bulkSlot={opts.noBulk ? null : (
        <ActionsMenu
          items={bulkItems}
          triggerIcon={<FaListUl />}
          triggerAriaLabel="Bulk actions for all visible cams"
          header={`Apply to all ${visibleSlots.length} visible cam${visibleSlots.length > 1 ? 's' : ''}`}
          align="right"
          openUp
        />
      )}
    />
  );

  // The two composite-timeline mounts (union band + per-camera multi bands); CSS gates which shows.
  // Factored so the portrait Option-A timeline card AND the landscape branch render the same pair. S20.
  // Single-cam row highlight (David 2026-08-28, this scenario ONLY): when we double-click a multicam
  // cell into single-cam view (zoomedSlot set — never conf-1, which can't zoom) AND the timeline is
  // EXPANDED, persistently highlight that cam's timeline row so the user knows which row they're viewing.
  // Falls back to the normal hover highlight otherwise; collapsed timeline (one bar) gets nothing. The
  // grid CELL highlight stays on hover alone below, so the zoomed full-screen cell gets no stray border.
  const timelineHighlightRow = (zoomedSlot != null && timelineExpanded) ? zoomedSlot : hoveredRow;
  const renderTimelines = (hidePlayheadLabel = false) => (
    <>
      <div className="mcpb-timeline-mount mcpb-timeline-mount--union">
        <Timeline
          clips={unionClips}
          windowStartTs={windowStartTs}
          windowLenSec={windowLenSec}
          offsetSec={offsetSec}
          onSeek={seek}
          visibility={vizVisibility}
          notice={timelineNotice}
          cacheByClip={cacheByClip}
          pillOwnRow
          hidePlayheadLabel={hidePlayheadLabel}
        />
      </div>
      <div className="mcpb-timeline-mount mcpb-timeline-mount--multi">
        <Timeline
          tracks={timelineTracks}
          windowStartTs={windowStartTs}
          windowLenSec={windowLenSec}
          offsetSec={offsetSec}
          onSeek={seek}
          visibility={vizVisibility}
          notice={timelineNotice}
          cacheByClip={cacheByClip}
          hidePlayheadLabel={hidePlayheadLabel}
          hoveredRow={timelineHighlightRow}
          onBandHover={setHoveredRow}
        />
      </div>
    </>
  );

  // The timeline EXPAND toggle — union band ↔ per-camera track ROWS. One factory so every tier (desktop,
  // tablet-landscape, portrait) uses the SAME control + glyph. STATE-BASED fold/unfold icons (David
  // #86bb7phhh S32): MdUnfoldMore = "expand into per-camera rows" when collapsed, MdUnfoldLess = "fold back to
  // one bar" when expanded — meaningful for the timeline (NOT the old FaExpandAlt ⤢, which read as FULLSCREEN).
  // Caller gates on canExpandTimeline (>1 visible cam).
  const renderExpandButton = () => (
    <button
      type="button"
      className={`mcpb-expand-btn${timelineExpanded ? ' mcpb-expand-btn--on' : ''}`}
      aria-label={timelineExpanded ? 'Collapse to one timeline' : 'Expand to per-camera rows'}
      title={timelineExpanded ? 'Collapse to one timeline' : 'Expand to per-camera rows'}
      aria-pressed={timelineExpanded}
      onClick={() => setTimelineExpanded((v) => !v)}
    >
      {timelineExpanded ? <MdUnfoldLess aria-hidden="true" /> : <MdUnfoldMore aria-hidden="true" />}
    </button>
  );

  // conf-1 camera select (#86bb7phhh S32, David): at conf-1 the shared CamButtonsView degrades into a
  // per-camera scroller (one number-only button per cam; on the narrow strip its ‹ › don't render + the
  // overflow scrollbar adds height and breaks the bar's alignment). Replace it with a compact
  // ‹ [current camera NAME] › stepper that opens the searchable CameraPicker (sheet on tablet / modal on
  // desktop). `setCamViews` at conf-1 is one entry per camera, so a ±1 step pages one camera; the picker
  // jumps to any. Multi-cam (conf>1) keeps the CamButtonsView bank strip — a few pages, works fine.
  // Camera NUMBER + descriptive label for a camera. The number is what the old conf-1 buttons showed
  // (cam.bID — David wants it back alongside the name); the label is the eView "NVR : Name" or the local
  // name. Kept local (not folded into the shared cameraDisplayLabel, which many surfaces use) so the
  // picker + stepper can render a distinct number badge. #86bb7phhh S32.
  const camParts = (cam) => {
    if (!cam) return { num: '', label: '' };
    const num = cam.bID != null ? cam.bID : (cam.bCamera != null ? cam.bCamera : '');
    const name = cam.sName || '';
    const label = (isEview && cam.bSerial) ? `${cam.sServerName || 'NVR'} : ${name}` : name;
    return { num: num !== '' ? String(num) : '', label: label || '' };
  };
  const camRowForSet = (set) => camParts(getVisibleCameras(cameras, set)[0]);
  const stepCam = (dir) => {
    const list = setCamViews || [];
    const i = list.indexOf(currentCamView);
    if (i < 0) return;
    const j = i + dir;
    if (j >= 0 && j < list.length) doCamViewChange(list[j]);
  };
  const renderCameraSelect = () => {
    if (layoutConf !== 1) return <div className="mcpb-set-strip"><CamButtonsView hideAutoScan /></div>;
    const list = setCamViews || [];
    const idx = list.indexOf(currentCamView);
    const cur = camParts((slots && slots[0]) || getVisibleCameras(cameras, currentCamView)[0]);
    return (
      <div className="mcpb-camsel">
        <button type="button" className="mcpb-camsel__step" onClick={() => stepCam(-1)} disabled={idx <= 0} aria-label="Previous camera" title="Previous camera"><FaAngleLeft aria-hidden="true" /></button>
        <button type="button" className="mcpb-camsel__current" onClick={() => setCamPickOpen(true)} aria-haspopup="dialog" aria-expanded={camPickOpen} title="Choose a camera">
          {cur.num !== '' && <span className="mcpb-camsel__num">{cur.num}</span>}
          <span className="mcpb-camsel__name">{cur.label || 'Select camera'}</span>
          <FaCaretDown className="mcpb-camsel__caret" aria-hidden="true" />
        </button>
        <button type="button" className="mcpb-camsel__step" onClick={() => stepCam(1)} disabled={idx < 0 || idx >= list.length - 1} aria-label="Next camera" title="Next camera"><FaAngleRight aria-hidden="true" /></button>
      </div>
    );
  };

  return (
    // is-entry / is-grid: lets the tablet-landscape tier keep the bottom nav in the entry state (where
    // the sidebar — which carries nav in the grid state — isn't mounted yet).
    <div className={`${containerClass} ${!hasRunnableSearch ? 'is-entry' : 'is-grid'}`} data-quick-actions={quickActionsAlways ? 'always' : 'hover'}>
      {/* Mobile/tablet top bar — hidden >= 1280 via CSS (the desktop boundary) */}
      {/* Reuse the SAME shared MobileTopBar as Live (#86bb7phhh) — tablet gets Jump + Views crumbs + ambient
          (SN·Days·online) + ⓘ + account sheet. Jump opens the shared JumpControl sheet (re-auth into the
          picked system), same as Live; gated on hasJump so cloud omits it. Mobile keeps the static name (CSS). */}
      <PlaybackTopBar
        sName={sName}
        bSerial={bSerial}
        fEview={fEview}
        userName={sUserName}
        daysRec={daysRec}
        usersOnline={usersOnline}
        onInfoClick={() => setSysInfoOpen(true)}
        onViews={() => setViewsOpen(true)}
        onJump={() => setJumpOpen(true)}
        canJump={getProductConfig().hasJump}
        viewDirty={viewDirty}
        viewLabel={currentView && !String(currentView).startsWith('__synthetic_') ? currentView : 'Views'}
      />

      {/* Desktop + tablet-landscape header — the SAME DesktopHeader as Live (reused via additive props),
          Playback active. Jump + Views crumbs (Jump gated on hasJump → cloud omits it, so the crumb is
          view-only there). Hidden < 1280 via .live-desktop-header CSS, so tablet (both orientations) + mobile keep
          MobileTopBar above. Crumbs open the shared JumpControl / ViewList sheets. #86bb7phhh */}
      <div className="live-desktop-header">
        <DesktopHeader
          page="playback"
          activePanel={viewsOpen ? 'views' : jumpOpen ? 'jump' : null}
          onOpenPanel={(key) => {
            // Crumb re-click TOGGLES the panel closed (#86bb7phhh, David: close via the crumb OR the panel ×);
            // one panel open at a time. The mobile top bar uses its own onViews/onJump (always-open) + the
            // sheet's own onClose, so this toggle only drives the >=1024 slide-out.
            if (key === 'views') { setJumpOpen(false); setViewsOpen(o => !o); }
            else if (key === 'jump') { setViewsOpen(false); setJumpOpen(o => !o); }
          }}
          onLive={() => history.push('/live')}
          onSetup={openSetup}
          viewLabel={currentView && !String(currentView).startsWith('__synthetic_') ? currentView : 'Views'}
          viewDirty={viewDirty}
        />
      </div>

      {/* Tablet-landscape + desktop (>=1024): a flex ROW so the Jump/Views slide-out PUSHES the video+timeline
          narrower, exactly like Live's .live-main-row. Below 1024 the panel isn't rendered (isDesktopChrome
          false) → this is just a full-width main and Jump/Views fall back to the bottom sheet. #86bb7phhh */}
      <div className="mcpb-main-row">
        {isDesktopChrome && (
          <div className={'live-dpanel' + ((viewsOpen || jumpOpen) ? ' live-dpanel--open live-dpanel--' + (viewsOpen ? 'views' : 'jump') : '')}>
            {/* Reuses Live's GLOBAL .live-dpanel/.live-dpane shell + the SHARED ViewList/JumpControl, so the two
                screens can't drift on styling or list logic. PERSIST (David): selecting a view / jumping does NOT
                close the panel (no onClose/onJumpStarted) — close via the crumb re-click (onOpenPanel toggle) or
                the panel ×. Arrange/New/✎ route into Live's arrange editor and return (Option A). */}
            {viewsOpen && (
              <div className="live-dpane">
                <div className="live-dpane__hd"><b>Views</b><button className="live-dpane__x" onClick={() => setViewsOpen(false)} aria-label="Close">&times;</button></div>
                <div className="live-dpane__body">
                  <ViewList
                    onArrange={(v) => { setViewsOpen(false); history.push('/live', { arrangeRequest: { view: v }, returnTo: '/mcplayback' }); }}
                    onArrangeCurrent={() => { setViewsOpen(false); history.push('/live', { arrangeRequest: 'current', returnTo: '/mcplayback' }); }}
                    onNewView={() => { setViewsOpen(false); history.push('/live', { arrangeRequest: 'new', returnTo: '/mcplayback' }); }}
                    skipDirtyGuard
                    isPlayback
                  />
                </div>
              </div>
            )}
            {jumpOpen && (
              <div className="live-dpane">
                <div className="live-dpane__hd"><b>Jump to system</b><button className="live-dpane__x" onClick={() => setJumpOpen(false)} aria-label="Close">&times;</button></div>
                <div className="live-dpane__body">
                  <JumpControl variant="list" />
                </div>
              </div>
            )}
          </div>
        )}
      <div className="mcplayback-main">
        {/* The grid is mounted whenever there is a runnable search — it stays behind the entry MODAL
            during a re-search ("Change") so the playback context is preserved. */}
        {hasRunnableSearch && (
          <>
            {/* Compact time control — committed time + an EXPLICIT "Change" CTA (not a bare chip). */}
            <div className="mcpb-time-control">
              <div className="mcpb-time-control__info">
                <span className="mcpb-time-control__label">Watching</span>
                <span className="mcpb-time-control__time">{chipTime(selectedTimestamp)}</span>
                <span className="mcpb-time-control__date">{chipDate(selectedTimestamp, nvrDay ? `${nvrDay}000000` : '')}</span>
                <span className="mcpb-time-control__window">{windowLabel(searchWindow)}</span>
              </div>
              <button
                className="mcpb-time-control__change"
                type="button"
                onClick={() => setTimeModalOpen(true)}
                aria-label="Change time"
              >
                <FaSlidersH aria-hidden="true" />
                <span>Change</span>
              </button>
            </div>

            {/* Workspace: portrait/desktop = single column; mobile-landscape = a 2-col grid
                (stage | 144px rail) via the existing phone-landscape query — no new breakpoint. */}
            {/* ONE DndContext wraps the whole workspace (David 2026-06-23) so every drag source — grid
                cells, sidebar pills, and the camera tree — and their cell drop targets share it. */}
            <DndContext sensors={dndSensors} collisionDetection={closestCenter} onDragStart={onDndDragStart} onDragEnd={onDndDragEnd} onDragCancel={onDndDragCancel}>
            <div className="mcpb-workspace">
            <div className="mcpb-stage">
            {/* onPointerDown reveals the auto-hidden floating transport in landscape (3d-2); it only sets
                state (no preventDefault/stopPropagation), so the cells' double-tap-zoom + drag still fire. */}
            <div ref={viewerRef} className={`mcpb-viewer mcpb-viewer--conf-${layoutConf}${conf2Stack && layoutConf === 2 ? ' mcpb-viewer--conf-2-stack' : ''}`} onTouchStart={onGridTouchStart} onTouchEnd={onGridTouchEnd}>
              <div className={`mcpb-grid mcpb-grid--conf-${layoutConf}${zoomedSlot != null ? ' mcpb-grid--zoomed' : ''}`}>
                {/* Render the VISIBLE layout (layoutConf cells), pulling each slot's cam; cams beyond
                    layoutConf stay loaded but are reachable only via the cam-strip / drag. An empty
                    visible slot shows the "+ Add camera" prompt (and grows the grid when filled). */}
                {Array.from({ length: layoutConf }, (_, i) => {
                  const cam = (slots || [])[i] || null;
                  const key = cam ? `${cam.bSerial}_${cam.bCamera}` : null;
                  const clips = (key && visibleTrackEvents && visibleTrackEvents[key]) || [];
                  const targetServer = cam && serverSessions ? serverSessions[cam.bSerial] : null;
                  const isServingNvr = !!cam && String(cam.bSerial) === String(bSerial);
                  // Per-slot profile (2d): the zoomed cell goes full-res (1); the rest keep the down-res
                  // 480p (2) at layoutConf>1 so hiding/showing them doesn't churn streams.
                  const cellStreamCtx = {
                    ...streamCtx,
                    playbackServerUrl: targetServer && targetServer.serverUrl,
                    playbackServerSerial: cam && cam.bSerial,
                    currentServerSerial: bSerial,
                    sSess: (targetServer && targetServer.sSess) || (isServingNvr ? sSess : null),
                    profile: (layoutConf > 1 && zoomedSlot !== i) ? 2 : 1,
                  };
                  // Cloud pro2/pro1 per-tile choice (#86bbmxrtt): multi-cam tiles prefer the lighter
                  // pro2 twin to cut decode load; conf-1, a zoomed-to-single cell, and the conf-8 desktop
                  // hero use pro1 for detail. Layout-only — whether the twin actually exists is the clip's
                  // concern (SlotCell reads its sMoviePro2; the player falls back to pro1 on a 404).
                  const preferPro2 = slotPrefersPro2({ conf: layoutConf, slotIndex: i, zoomedSlot, isDesktop });
                  return (
                    <SlotCell
                      key={i}
                      camera={cam}
                      camLabel={cameraDisplayLabel(cam, { isEview })}
                      slotClips={clips}
                      windowStartTs={windowStartTs}
                      offsetSec={offsetSec}
                      playing={playing}
                      holding={holding}
                      speed={speed}
                      streamCtx={cellStreamCtx}
                      preferPro2={preferPro2}
                      zoomed={zoomedSlot === i}
                      dimmed={zoomedSlot != null && zoomedSlot !== i}
                      canZoom={layoutConf > 1}
                      onZoom={() => handleCellZoom(i)}
                      onViewSingle={() => toggleZoom(i)}
                      selected={layoutConf > 1 && selectedSlot === i}
                      onSelect={() => handleSelectSlot(i)}
                      actionsShown={selectedSlot === i}
                      alwaysShowQuickActions={quickActionsAlways}
                      compact={isCompactCell(i)}
                      hovered={hoveredRow === i}
                      dragEnabled={layoutConf > 1}
                      loading={eventsLoading}
                      slotIndex={i}
                      onStall={onStall}
                      onVideoEl={registerVideo}
                      onCacheProgress={onCacheProgress}
                      showVideoInfo={videoInfo}
                      onToggleVideoInfo={toggleVideoInfo}
                      canExport={!!fExport}
                      flagEvent={flagEvent}
                      pushToast={pushToast}
                      startDownload={startDownload}
                      onAddCamera={() => openDrawer(i, false)}
                      onChangeCamera={() => openDrawer(i, true)}
                      onRemoveFromView={() => handleUnassign(i)}
                      onMediaError={doVerifySession}
                    />
                  );
                })}
                {/* Persistent, not a Toast: it stays until the window has a clip to play. */}
                {emptyWindowNotice && (
                  <div className="mcpb-empty-window" role="status">{emptyWindowNotice}</div>
                )}
              </div>
              {/* Floating ghost that follows the cursor/finger (the mock's native-dnd feel). Renders a
                  cell-sized identity tile of the cam being dragged (cell/pill = its frame + slot color;
                  tree = name-only); the source cell/pill dims meanwhile. */}
              <DragOverlay dropAnimation={null}>
                {activeDrag && activeDrag.cam ? (
                  <div className="mcpb-cell mcpb-drag-ghost" style={activeDrag.colorVar ? { '--cell-cam-color': activeDrag.colorVar } : undefined}>
                    {activeDrag.frame && <img className="mcpb-drag-ghost__img" src={activeDrag.frame} alt="" />}
                    <span className="mcpb-cell__cam-name">{cameraDisplayLabel(activeDrag.cam, { isEview })}</span>
                  </div>
                ) : null}
              </DragOverlay>

              {/* Camera SET selector + transport. Portrait: stacked (set-strip above the transport, as
                  before). Mobile-LANDSCAPE: .mcpb-playrow flips to ONE row — transport (streamlined:
                  playback + speed) on the left + the scrollable set-strip on the right — while the
                  transport's conf/bulk/options move to the rail (David #86bb7phhh). CamButtonsView reused
                  verbatim; a swipe on the grid pages the same set. */}
              {(isLandscape || isDesktop) ? (
                /* Phone-landscape (rail) + DESKTOP (≥1280, single-row condense) render NO separate camera bar
                   here — desktop folds layout + banks + bulk/options into the ONE control row in the
                   timeline-card head below; phone-landscape uses the rail. Tablet-landscape + portrait render
                   the camera bar (layout + banks + bulk/options) here. #86bb7phhh */
                null
              ) : (
                /* Option A grid-composition row (portrait, #86bb7phhh S20): Layout (left) + the cam-set
                   banks + Options cog (right). Playback moves to the timeline card below. Landscape keeps
                   its own .mcpb-playrow (above) + rail, untouched. */
                <div className="mcpb-gcrow">
                  {/* Sets row (Mock B, #86bb7phhh): Layout (left) · cam-set banks (fill + scroll, own ‹ ›) ·
                      Bulk + Options (right). Bulk + Options are CSS-hidden on mobile — they move to the ⋯
                      More sheet — and shown inline on tablet+. Views moved to the bottom row on mobile /
                      the header crumb on tablet, so it is no longer on this row. */}
                  {renderTransport(['layout'], 'mcpb-transport--gc mcpb-transport--gc-layout')}
                  {renderCameraSelect()}
                  {renderTransport(['options'], 'mcpb-transport--gc mcpb-transport--gc-options')}
                </div>
              )}
            </div>

            {/* Legacy 4-slot LoadedCamPills portrait mount removed (#86bb7phhh S20) — it was always
                display:none in the stage (.mcpb-stage .mcpb-cam-strip-wrap, mcplaybackStyle.css) since the
                view-based rebuild; cam sets are managed via the banks row. The COMPONENT stays for the
                landscape rail + desktop sidebar (progressive 4-cam teardown, per David). */}

            {isDesktop ? (
              /* DESKTOP (≥1280, #86bb7phhh S31): ONE control row above the ruler (David: condense the two
                 wide-tier rows into one on desktop only). Composes the SAME parts the other tiers use —
                 camera-selection (layout + banks), transport, time-nav, and refine/act (Filters + expand +
                 Bulk/Options) — divider-separated; the ruler sits below. Tablet-landscape keeps its TWO-row
                 layout in the branch below, unchanged. Sizing/spacing = the .mcpb-deskbar @media(≥1280) block. */
              <div className={`mcpb-timeline-card mcpb-timeline-card--desk${timelineExpanded && canExpandTimeline ? ' mcpb-timeline-card--expanded' : ''}`}>
                {/* Desktop control bar — the ONE row, now with a small uppercase label over each control
                    GROUP (David #86bb7phhh S32: playback has more functions than Live, so name what each
                    cluster applies to). Each group is a compact [label]/[controls] COLUMN — not a full extra
                    row on top — so the bar grows only ~14px. Desktop-only (this branch). */}
                <div className="mcpb-timeline-card__head mcpb-deskbar">
                  {/* CAMERAS — the layout picker STACKED over the cam-set banks (David #86bb7phhh S32): the
                      one-row sets strip showed only ~2 of N banks with no reachable ‹ ›. Stacking gives the
                      banks their own ~30px line (with ‹ › + scroll) while keeping the section compact — no
                      full extra row. The grid shows its LAYOUT label here (widened) so it reads as the layout
                      control above the banks. */}
                  <div className="mcpb-deskgroup mcpb-deskgroup--cameras">
                    <span className="mcpb-deskgroup__label">Cameras</span>
                    <div className="mcpb-deskgroup__stack">
                      {renderTransport(['layout'], 'mcpb-transport--gc mcpb-transport--gc-layout')}
                      {renderCameraSelect()}
                    </div>
                  </div>
                  <span className="mcpb-cardhead-divider" aria-hidden="true" />
                  {/* PLAYBACK — 10s-back / play / 10s-fwd + elapsed + speed. */}
                  <div className="mcpb-deskgroup mcpb-deskgroup--playback">
                    <span className="mcpb-deskgroup__label">Playback</span>
                    <div className="mcpb-deskgroup__row">
                      {renderTransport(['playback'], 'mcpb-transport--card')}
                    </div>
                  </div>
                  <span className="mcpb-cardhead-divider" aria-hidden="true" />
                  {/* DATE & TIME — the go-to-date calendar + day/window paging arrows (navigate TO a moment). */}
                  <div className="mcpb-deskgroup mcpb-deskgroup--datetime">
                    <span className="mcpb-deskgroup__label">Date &amp; Time</span>
                    <div className="mcpb-deskgroup__row">
                      {renderRibbon(['when'])}
                    </div>
                  </div>
                  <span className="mcpb-cardhead-divider" aria-hidden="true" />
                  {/* SPAN — how much time the timeline shows (1h/4h/8h/24h). Its OWN label + divider so it no
                      longer reads as part of the paging arrows next to it (David #86bb7phhh S32). */}
                  <div className="mcpb-deskgroup mcpb-deskgroup--span">
                    <span className="mcpb-deskgroup__label">Span</span>
                    <div className="mcpb-deskgroup__row">
                      {renderRibbon(['zoom'])}
                    </div>
                  </div>
                  <span className="mcpb-cardhead-divider" aria-hidden="true" />
                  {/* TOOLS — Filters + Bulk/Options (pinned right). The timeline expand/compact toggle was
                      pulled OUT of this row (David #86bb7phhh S32 — it'll be relocated nearer the timeline;
                      TBD) to buy the CAMERAS stack a little width. Still present on the other tiers. */}
                  <div className="mcpb-deskgroup mcpb-deskgroup--tools">
                    <span className="mcpb-deskgroup__label">Tools</span>
                    <div className="mcpb-deskgroup__row mcpb-tools-row">
                      <span className="mcpb-tools-pill" onClick={e => { if (e.target.closest('button')) return; const btn = e.currentTarget.querySelector('button'); if (btn) btn.click(); }}>{renderRibbon(['filters'])}<span className="mcpb-tools-pill__label">Filters</span></span>
                      <span className="mcpb-tools-pill" onClick={e => { if (e.target.closest('button')) return; const btn = e.currentTarget.querySelector('button'); if (btn) btn.click(); }}>
                        <ActionsMenu
                          items={bulkItems}
                          triggerIcon={<FaListUl />}
                          triggerAriaLabel="Bulk actions for all visible cams"
                          header={`Apply to all ${visibleSlots.length} visible cam${visibleSlots.length > 1 ? 's' : ''}`}
                          align="right"
                          openUp
                        />
                        <span className="mcpb-tools-pill__label">Bulk</span>
                      </span>
                      <span className="mcpb-tools-pill" onClick={e => { if (e.target.closest('button')) return; const btn = e.currentTarget.querySelector('button'); if (btn) btn.click(); }}>{renderTransport(['options'], 'mcpb-transport--gc mcpb-transport--gc-options', { noBulk: true })}<span className="mcpb-tools-pill__label">Options</span></span>
                    </div>
                  </div>
                </div>
                {/* Timeline with the expand toggle pinned to its RIGHT END (David #86bb7phhh S32) — ties the
                    control to the timeline it drives (the real fix behind Task B). Top-anchored
                    (align-items:flex-start) so on expand it holds the top-right of the taller per-camera rows,
                    and on collapse it sits at the end of the single bar. Shortens the ruler by its width. */}
                <div className="mcpb-timeline-row">
                  <div className="mcpb-timeline-row__area">{renderTimelines(true)}</div>
                  {canExpandTimeline && <div className="mcpb-timeline-row__expand">{renderExpandButton()}</div>}
                </div>
              </div>
            ) : isWideNoRail ? (
              /* TABLET-LANDSCAPE (David 2026-08-10): NO rail — full-width video + a full-width bottom bar
                 (Live-desktop pattern). The camera bar (above) holds layout + banks + bulk/options; this
                 timeline CARD holds the playback strip (LEFT) + a time-nav cluster (RIGHT: go-to-date +
                 day/window paging + span + filters + expand) with the composite timeline below. The event
                 "show on timeline" toggles fold into Filters. Separators group the clusters (WIP — refine). */
              <div className={`mcpb-timeline-card${timelineExpanded && canExpandTimeline ? ' mcpb-timeline-card--expanded' : ''}`}>
                <div className="mcpb-timeline-card__head">
                  {renderTransport(['playback'], 'mcpb-transport--card')}
                  <span className="mcpb-cardhead-divider" aria-hidden="true" />
                  {/* Date/time + span = one group; a divider separates it from Filters, and Filters from
                      Expand, so each cluster reads as its own container (David #86bb7phhh). */}
                  <div className="mcpb-timenav__group">
                    {renderRibbon(['when'])}
                    {renderRibbon(['zoom'])}
                  </div>
                  <span className="mcpb-cardhead-divider" aria-hidden="true" />
                  {renderRibbon(['filters'])}
                  {canExpandTimeline && (
                    <>
                      <span className="mcpb-cardhead-divider" aria-hidden="true" />
                      {renderExpandButton()}
                    </>
                  )}
                </div>
                {renderTimelines(true)}
              </div>
            ) : isLandscape ? (
              <>
                {/* Phone-LANDSCAPE: the playback strip + cam-set banks share the card head; the composite
                    timeline below; the ribbon (Views/Filters/span/when) + nav live in the rail. #86bb7phhh */}
                <div className="mcpb-timeline-card">
                  <div className="mcpb-timeline-card__head">
                    {renderTransport(['playback'], 'mcpb-transport--card')}
                    <span className="mcpb-cardhead-divider mcpb-cardhead-divider--sets" aria-hidden="true" />
                    {renderCameraSelect()}
                  </div>
                  {renderTimelines(true)}
                </div>
                <TimelineVisibilityRow visibility={vizVisibility} onToggle={toggleViz} onShowAll={showAllViz} />
              </>
            ) : (
              /* Option A portrait (#86bb7phhh S20): the timeline CARD groups the playback strip WITH the
                 composite timeline so the controls visibly drive it (Nx/UniFi pattern). Filters/zoom/when
                 still ride the ribbon below for now — their relocation onto the card + a "Go to date & time"
                 hero row is the next slice. */
              <>
                {/* Timeline CARD: head = playback strip + Filters funnel + zoom; the composite timeline
                    below. The controls visibly drive the timeline (Option A). */}
                <div className={`mcpb-timeline-card${timelineExpanded && canExpandTimeline ? ' mcpb-timeline-card--expanded' : ''}`}>
                  <div className="mcpb-timeline-card__head">
                    {renderTransport(['playback'], 'mcpb-transport--card')}
                    {/* Separator — the transport (playback) and the timeline-tools (Filters/zoom/expand) are
                        two different jobs; a divider keeps them from reading as one run-on strip (David S20). */}
                    <span className="mcpb-cardhead-divider" aria-hidden="true" />
                    <div className="mcpb-timeline-card__tools">
                      {renderRibbon(['filters'])}
                      {renderRibbon(['zoom'])}
                      {/* ⋯ More — MOBILE only (CSS-hidden ≥768). Opens the Speed/Filters/Bulk/Options sheet;
                          on tablet+ those are inline (Filters here, Speed in the transport, Bulk + Options
                          on the banks row) so the trigger hides. No expand button on mobile. Mock B, #86bb7phhh. */}
                      <button
                        type="button"
                        className="mcpb-more-trigger"
                        aria-label="More controls"
                        aria-haspopup="dialog"
                        aria-expanded={moreOpen}
                        onClick={() => setMoreOpen(true)}
                      >
                        <FaEllipsisH aria-hidden="true" />
                      </button>
                      {canExpandTimeline && renderExpandButton()}
                    </div>
                  </div>
                  {renderTimelines(true)}
                </div>
                <TimelineVisibilityRow visibility={vizVisibility} onToggle={toggleViz} onShowAll={showAllViz} />
                {/* "Go to date & time" hero row — the primary time navigator (calendar WhenButton + day/window
                    paging), labeled so it clearly reads as how you pull up footage at a time (David S20). */}
                <div className="mcpb-whenrow">
                  {/* Bottom thumb-zone row. MOBILE (Mock B): a single row — Views select (left, shows the
                      current view name) + WhenButton date/time (right); the label + the day/window arrows
                      hide via CSS. TABLET+: the approved column — uppercase label over the day/window nav +
                      hero WhenButton (Views hidden here; the header crumb owns it). #86bb7phhh */}
                  <div className="mcpb-whenrow__label">Go to date &amp; time</div>
                  <div className="mcpb-whenrow__views">{renderRibbon(['views'])}</div>
                  {renderRibbon(['when'])}
                </div>
                {moreOpen && (
                  <MoreSheet
                    speed={speed}
                    onSpeed={setSpeed}
                    filters={filters}
                    onSetFilters={doSetFilters}
                    bulkItems={bulkItems}
                    autoSkip={autoSkip}
                    onToggleAutoSkip={toggleAutoSkip}
                    videoInfo={videoInfo}
                    onToggleVideoInfo={toggleVideoInfo}
                    quickActionsAlways={quickActionsAlways}
                    onToggleQuickActions={toggleQuickActions}
                    onClose={() => setMoreOpen(false)}
                  />
                )}
              </>
            )}
            </div>

            {/* Mobile-LANDSCAPE right rail — mounted ONLY in phone-landscape (isLandscape) so portrait/tests
                don't render its reused ribbon + transport a second time. Carries the portrait control ribbon
                (Views/Filters/span/date-time), the transport's conf/bulk/options cluster, + the shared
                MobileBottomNav (Live/Playback/Setup) — reused verbatim so landscape matches portrait. */}
            {isLandscape && (
              <MCPlaybackRail
                clock={hasNvrClock ? (
                  <PlaybackRailClock daysRec={daysRec} onInfo={() => setSysInfoOpen(true)} />
                ) : null}
                body={(
                  <>
                    <div className="mcpb-rail__group mcpb-rail__group--watch">
                      {renderRibbon(['views'])}
                      {renderTransport(['layout'], 'mcpb-transport--railgrid')}
                    </div>
                    <div className="mcpb-rail__group mcpb-rail__group--time">
                      {renderRibbon(['when'])}
                      {renderRibbon(['zoom'])}
                    </div>
                    <div className="mcpb-rail__group mcpb-rail__group--act">
                      {renderRibbon(['filters'])}
                      {renderTransport(['options'], 'mcpb-transport--railact')}
                    </div>
                  </>
                )}
                nav={(
                  <MobileBottomNav
                    activePage="mcplayback"
                    fPlayback={fPlayback}
                    mobileMenuOpen={mobileMenuOpen}
                    onToggleMenu={toggleMobileMenu}
                    onSetup={openSetup}
                  />
                )}
              />
            )}

            {/* No right sidebar at any tier. Desktop (≥1280) now renders the same no-rail full-width model
                as tablet-landscape (the WIDE_NORAIL_MQ branch above); the deprecated MCPlaybackDesktopSidebar
                (Add-camera drawer + Saved Layouts + camera tree + footer nav) was removed — cameras are
                driven by the selected Live view (#86bb7phhh S31). */}
            </div>
            </DndContext>
          </>
        )}

        {/* Before the first view loads, the grid area shows a loading state (not the old entry modal). */}
        {!hasRunnableSearch && (
          <div className="mcpb-loading-screen" role="status">
            <span className="mcpb-loading-screen__label">Loading recorded video&hellip;</span>
          </div>
        )}

        {/* Time-nav modal — change the window/filters for the CURRENT view's cameras (no camera select;
            cameras come from the shared view). A bridge until the mobile control ribbon owns this inline. */}
        {timeModalOpen && (
          <PlaybackDtModal
            initial={{ selectedTimestamp, window: searchWindow, filters }}
            onGo={handleChangeTime}
            onClose={() => setTimeModalOpen(false)}
          />
        )}

        {/* System-details sheet — opened by the rail clock's ⓘ (David #86bb7phhh). The SAME self-contained
            sheet Live uses (server slice via connect), so both rails share it. */}
        <SystemInfoSheet visible={sysInfoOpen} onClose={() => setSysInfoOpen(false)} />
        {/* Views sheet for the tablet header crumb — the SAME shared ViewList as Live + the ribbon;
            Arrange/New/✎ route into Live's arrange editor and return (Option A). #86bb7phhh */}
        {!isDesktopChrome && viewsOpen && (
          <Sheet className="mcpb-sheet mcpb-sheet--views" scrimClassName="mcpb-sheet-scrim" title="Views" ariaLabel="Views" onClose={() => setViewsOpen(false)}>
            <ViewList
              onArrange={(v) => { setViewsOpen(false); history.push('/live', { arrangeRequest: { view: v }, returnTo: '/mcplayback' }); }}
              onArrangeCurrent={() => { setViewsOpen(false); history.push('/live', { arrangeRequest: 'current', returnTo: '/mcplayback' }); }}
              onNewView={() => { setViewsOpen(false); history.push('/live', { arrangeRequest: 'new', returnTo: '/mcplayback' }); }}
              onClose={() => setViewsOpen(false)}
              skipDirtyGuard
              isPlayback
            />
          </Sheet>
        )}
        {/* Jump sheet for the header crumb — the SAME shared JumpControl as Live (re-auth into the picked
            system; product-clean: renders nothing when hasJump is false). #86bb7phhh */}
        {!isDesktopChrome && jumpOpen && (
          <Sheet className="mcpb-sheet mcpb-sheet--jump" scrimClassName="mcpb-sheet-scrim" title="Jump" ariaLabel="Jump to system" onClose={() => setJumpOpen(false)}>
            <JumpControl variant="list" onJumpStarted={() => setJumpOpen(false)} />
          </Sheet>
        )}
        {/* Camera picker (conf-1) — searchable list of the view's cameras by NAME; the ‹ [name] › stepper in
            the camera bar opens it. Sheet on tablet / modal on desktop via .mcpb-sheet--campick. #86bb7phhh */}
        {camPickOpen && (
          <Sheet className="mcpb-sheet mcpb-sheet--campick" scrimClassName="mcpb-sheet-scrim" title="Select camera" ariaLabel="Select camera" onClose={() => setCamPickOpen(false)}>
            <CameraPicker
              sets={setCamViews}
              current={currentCamView}
              rowFor={camRowForSet}
              onSelect={(set) => { doCamViewChange(set); setCamPickOpen(false); }}
            />
          </Sheet>
        )}
      </div>
      </div>{/* /.mcpb-main-row */}

      <CameraDrawer
        open={drawer.open}
        onClose={closeDrawer}
        targetSlot={drawer.slot}
        replacing={drawer.replacing}
        onPick={handlePick}
        onUnassign={handleUnassign}
        savedLayouts={savedLayouts}
        atLayoutCap={atLayoutCap}
        onLoadLayout={handleLoadLayout}
        onSaveLayout={handleSaveLayout}
        onDeleteLayout={handleDeleteLayout}
        onRenameLayout={handleRenameLayout}
        onReplaceLayout={handleReplaceLayout}
      />

      {/* Action feedback + Undo + download progress (2c) — fixed bottom stack, scoped CSS. */}
      <Toasts
        toasts={allToasts}
        onDismiss={(id) => {
          // A download toast's ✕ CANCELS the in-flight download (aborts the fetch); every other toast just dismisses.
          if (typeof id === 'string' && id.indexOf('dl-') === 0) cancelDownload(Number(id.slice(3)));
          else dismissToast(id);
        }}
      />

      {/* Mobile bottom nav (parity with Live/Playback so mobile users can leave /mcplayback — David
          2026-06-19). activePage hides the current "Multi-cam" entry; hidden on desktop via shared CSS. */}
      <MobileBottomNav
        activePage="mcplayback"
        fPlayback={fPlayback}
        mobileMenuOpen={mobileMenuOpen}
        onToggleMenu={toggleMobileMenu}
      />
      {mobileMenuOpen === 'menu' && (
        <MobileMoreMenu
          sName={sName}
          platform={platform}
          fEview={fEview}
          sSess={sSess}
          logoutUser={doLogout}
          onClose={closeMobileMenu}
          onSetup={openSetup}
        />
      )}
    </div>
  );
};

// enabledSetCamViews builds a new array on every call, and connect() re-renders on any new reference, so
// every store update (the NVR clock among them) re-rendered the grid. Keep the result while its inputs hold.
let setCamViewsMemo = { server: null, conf: null, value: [] };
const memoSetCamViews = (server, conf) => {
  if (setCamViewsMemo.server !== server || setCamViewsMemo.conf !== conf) {
    setCamViewsMemo = { server, conf, value: enabledSetCamViews(server, conf) };
  }
  return setCamViewsMemo.value;
};

const mapStateToProps = (state) => {
  const { sSess, username, password } = state.auth;
  const { sName, bSerial, fEview, cloudAPI, sApiToken, serverSessions, machineStatus, usersOnline } = state.server;
  const { sName: sUserName, fExport, fPlayback } = state.user;
  const { platform } = state.utility;
  const {
    slots, trackEvents, eventsLoading, eventsError, selectedTimestamp, window: searchWindow, filters, playheadOffset,
  } = state.mcplayback;
  // conf is the SHARED selected grid — the SAME state.config.conf Live uses ('conf-N' string), mapped to a
  // number for the playback grid. Live and playback both read it; the transport picker writes it back via
  // configChange (#86bb7phhh). NOT derived from the camera count anymore.
  const conf = confToNumber(state.config.conf);
  const { currentView, pristineCameras, isDirty } = state.view;
  // Genuine unsaved rearrange vs the pristine saved layout (mirrors Live's liveViewDirty, #86bb7phhh):
  // gated on isDirty (set only on a real drag, reset on every jump/view-load) so a jump/view switch can't
  // false-light the dot; the fine arrangementsEqual check still CLEARS the dot when a reverted drag matches
  // the baseline. (playback shows the dot but never prompts to save — skipDirtyGuard.)
  const viewDirty = !!(!state.navigation.isJumping && isDirty && currentView && !String(currentView).startsWith('__synthetic_')
    && pristineCameras && pristineCameras.length
    && !arrangementsEqual(state.server.cameras || [], pristineCameras));
  // The grid is SHARED with Live (#86bb7phhh): cameras = state.server.cameras (view-arranged), and the
  // visible SET is that array sliced by the shared currentCamView (state.camera). Both drive the same grid.
  const { currentCamView } = state.camera;
  // The ENABLED sets for the current conf (drops empty banks) — drives the swipe so it pages only real sets,
  // matching the strip (CamButtonsView hideDisabled). Same state.server arrays the strip reads.
  const setCamViews = memoSetCamViews(state.server, state.config.conf);
  return {
    sSess, username, password, sName, bSerial, fEview, cloudAPI, sApiToken, sUserName, fExport, fPlayback,
    serverSessions, currentView, currentCamView, setCamViews, viewDirty, cameras: state.server.cameras,
    platform, machineStatus, usersOnline,
    conf, slots, trackEvents, eventsLoading, eventsError, selectedTimestamp, searchWindow, filters, playheadOffset: playheadOffset || 0,
    isEview: isMultiServerSession(state),
  };
};

export default connect(
  mapStateToProps,
  {
    getNvrTimestamp, runMcpbSearch, logoutUser, setMcpbSlotCamera, refetchMcpbEvents,
    setMcpbConf, setMcpbSlots, setMcpbPlayhead, setMcpbEventFlag, loadViewIntoMcpb, autoSelectDefaultView,
    setMcpbSpan, pageMcpbWindow, setMcpbFilters, getMcpbPresence, changeGridLayout, camViewChange, setCameraArrangement, revertViewToPristine,
    verifyPlaybackSession,
  },
)(MCPlaybackView);
